'use client';

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BranchThemeContext } from '@/components/customer-surface/BranchThemeScope';
import { useTheme } from '@/contexts/ThemeProvider';

import { useMenuChangeSignal } from '@/hooks/realtime/useMenuChangeSignal';
import { useParams, useSearchParams } from 'next/navigation';
import { customerChatEnabled } from '@/lib/config-system/customer-chat';
import { customerVoiceEnabled } from '@/lib/config-system/customer-voice';
import { customerFetch, chowAppUrl, useCustomerSession } from '@/lib/customer-bridge';
import type { Template } from '@/app/dashboard/templates/components/types/template';
import { templateOwnsPromotionPlacement } from '@/app/dashboard/templates/components/lib/templateCapabilities';
import {
  resolveGuestMenuRendererData,
  resolveTemplateSurfacePalette,
  resolveTemplateSurfaceStyle,
} from '@/app/dashboard/templates/components/lib/templateExperience';
import {
  CUSTOMER_UNIFIED_SHELL_CONFIG,
  dialogPresentationClassName,
  footerVisible,
  promotionFallbackVisible,
  resolveTemplatePromotionPresentationConfig,
} from '@/app/dashboard/templates/components/lib/templateContracts';
import type { TemplateRecord } from '@/hooks/menu/useTemplates';
import { trackSearchPerformed, trackCategoryViewed } from '@/lib/public-menu-analytics';
import { buildMenuData } from '@/app/dashboard/templates/components/lib/util';
import { applyDesignOverrides } from '@/lib/menu-themes/apply';
import { Loader2, TicketPercent, ArrowDown, Clock, UtensilsCrossed, ArrowUpRight } from 'lucide-react';
import type { OperatingMode } from '@/lib/operating-modes/keys';
import type { BrandingLevel } from '@/lib/billing/service';
import { 
  getBranchById,
  getBusinessById,
  getBranchMenuItemServiceOverrides,
  getMenuBySlug,
  getMenuCategories,
  getMenuItemAvailabilityData,
  getMenuCollections,
  getMenuItems,
  getMenuItemOptionGroups,
  getPromotions,
  getTemplate,
} from '@/lib/actions';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { PublicMenuPoweredFooter } from '@/components/menu/PublicMenuPoweredFooter';
import { PublicSessionStatusStrip } from '@/components/menu/PublicSessionStatusStrip';
import { useGuestOfflineSync } from '@/hooks/offline/useGuestOfflineSync';
import { type PublicMenuAction, type PublicMenuBranchDetails, type PublicMenuMode } from '@/components/menu/PublicMenuShell';
// The Place shell: one frame for every customer screen. Replaces
// PublicMenuShell's collapsible-panel chrome — see components/place/PlaceShell.tsx.
import { PlaceShell } from '@/components/place/PlaceShell';
import { PlaceLineTabs, type PlaceLineTab } from '@/components/place/PlaceLineTabs';
import type { MenuLineKind } from '@/lib/menu-lines/kinds';
import { PlaceProvider, useRegisterPlaceActions } from '@/components/place/PlaceProvider';
import { PlaceMenuNav } from '@/components/place/PlaceMenuNav';
import { useOrderableMenuRows, MENU_BODY_SELECTOR } from '@/components/menu/useOrderableMenuRows';
import { resolvePlaceState, MODE_WORDS } from '@/lib/customer/place-state';
import { describeModeChange, type OrderMode } from '@/lib/customer/order-context';
import { resolveFulfilmentPolicy } from '@/lib/customer/fulfilment-policy';
import { ArrivalSheet } from '@/components/place/ArrivalSheet';
import { resolvePlaceTabs, placePrimaryAction, midSentence } from '@/lib/customer/place-tabs';
import type { PlaceActionOption } from '@/lib/customer/place-action';
import { useCustomerAccountPrefetch } from '@/lib/customer/account-prefetch';
import { PlaceMenuBody } from '@/components/place/PlaceMenuBody';
import { PlaceMenuViewSwitcher } from '@/components/place/PlaceMenuViewSwitcher';
import { Suspense } from 'react';
import { resolveTemplateEngine } from '@/app/dashboard/templates/components/lib/templateEngine';
import { PlaceOrdersEntry } from '@/components/place/PlaceOrdersEntry';
import { buildPlaceBodyStyle, buildPlaceChromeStyle } from '@/lib/customer/place-chrome-style';
import { AiEntryPill } from '@/components/customer-ai/AiEntryPill';
import { PlaceSearchSheet } from '@/components/place/PlaceSearchSheet';
import { GuestV2MenuNav } from '@/components/place/GuestV2MenuNav';
import { GuestV2ProfileSheet } from '@/components/place/GuestV2ProfileSheet';
import { MenuLanguageSelector } from '@/components/menu/MenuLanguageSelector';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMenuItemImpressionTracker } from '@/components/menu/useMenuItemImpressionTracker';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useMenuTranslation, isOriginalLanguage } from '@/hooks/menu/useMenuTranslation';
import { useLivePlaceOrder } from '@/hooks/menu/useLivePlaceOrder';
import { MenuItemDetailSheet } from '@/components/menu/MenuItemDetailSheet';
import { GuestV2MenuItemSheet } from '@/components/place/GuestV2MenuItemSheet';
import { guestV2PaletteVars } from '@/components/guest-v2/palette';
import { PushOrderSync } from '@/components/customer-surface/PushOrderSync';
import { getGuestUsername } from '@/lib/customer/guest-identity';
import type { CartItem, OrderingMenuItem } from '@/lib/menu/ordering/types';
import { isDietaryProfileEmpty, useDietaryProfileSync } from '@/lib/menu/dietary';
import type { ResolverMenuIndex } from '@/lib/voice/resolver';
import { legacyMenuPath, placePath, placeSubPath } from '@/lib/routing/place-href';
import type { PlaceMenuViewMode } from '@/lib/customer/place-menu-view';
import {
  buildPlaceMenuViewHref,
  hasRenderableTemplateBody as hasRenderableTemplateBodyOf,
  parsePlaceMenuViewMode,
  resolvePlaceMenuView,
} from '@/lib/customer/place-menu-view';

/**
 * This page's mode key → the context model's (doc 70).
 *
 * Two vocabularies exist for one idea and neither can simply be renamed: this
 * page has said `'menu'` for browsing since before the Place shell, and it is
 * persisted in guests' sessionStorage and read by the template layer. The
 * context model says `'browse'`, because "menu" as a name for *not ordering
 * from the menu* is the confusion that produced the bug in the first place.
 *
 * One adapter at the boundary beats renaming a key that is already on disk in
 * other people's browsers.
 */
/**
 * The context model's mode key → this page's.
 *
 * The inverse of `toOrderContextMode`. `browse` maps back to `'menu'`, the name
 * this page has used since before the Place shell and which is persisted in
 * guests' sessionStorage — see the note on the forward adapter for why the two
 * vocabularies both survive.
 */
function fromOrderContextMode(mode: OrderMode): string {
  // `counter` maps to this page's `'pickup'`, which is how a till session is
  // already represented here — NOT to `'menu'`. Mapping it to browse would turn
  // a guest who chose "order at the counter" into one who cannot order at all.
  if (mode === 'counter') return 'pickup';
  return mode === 'browse' ? 'menu' : mode;
}

function toOrderContextMode(key: string): OrderMode {
  switch (key) {
    case 'dine_in':
      return 'dine_in';
    case 'pickup':
      return 'pickup';
    case 'delivery':
      return 'delivery';
    case 'scheduled':
      return 'scheduled';
    default:
      return 'browse';
  }
}

/**
 * What a guest means by "search the menu".
 *
 * Chow mobile matches the title AND the description
 * (`menuraq-chow-mobile` → app/place/[id]/menu.tsx). The web matched titles only,
 * so searching "coconut" missed "Groundnut soup — chicken, coconut milk, chilli"
 * even though the word was visible on the row. One predicate, used by both the
 * data filter and the analytics count, so the number can never disagree with
 * the list.
 */
function matchesMenuQuery(
  item: { title?: unknown; name?: unknown; description?: unknown },
  query: string,
): boolean {
  const title = typeof item.title === 'string' ? item.title : typeof item.name === 'string' ? item.name : '';
  const description = typeof item.description === 'string' ? item.description : '';
  return `${title} ${description}`.toLowerCase().includes(query);
}

// Heavy client islands, code-split out of the first-load bundle so a guest on
// café Wi-Fi gets the menu itself as fast as possible (doc 03 §3.1).
const PublicMenuOrderingExperience = dynamic(
  () => import('@/components/menu/PublicMenuOrderingExperience'),
  { ssr: false },
);
const PublicMenuPromotionClaimDialog = dynamic(
  () =>
    import('@/components/menu/PublicMenuPromotionClaimDialog').then(
      (mod) => mod.PublicMenuPromotionClaimDialog,
    ),
  { ssr: false },
);
// Sub-type-specific customer ordering flow for Standard business type.
// Replaces the generic PublicMenuOrderingExperience when the branch has a
// specific_type that maps to a Standard sub-type composition.
const SubTypeCustomerFlow = dynamic(
  () =>
    import('@/components/types/standard/customer/SubTypeCustomerFlow').then(
      (mod) => mod.SubTypeCustomerFlow,
    ),
  { ssr: false },
);
// Feedback island , only loads when the guest opens it.
const PublicMenuFeedbackDialog = dynamic(
  () => import('@/components/menu/PublicMenuFeedbackDialog'),
  { ssr: false },
);
// The Chow auth modal, mounted once for the V2 menu: the profile sheet's
// sign-in card and the checkout gate both open it without leaving the page,
// so the guest keeps their cart, table session and scroll position.
const CustomerAuthModal = dynamic(
  () => import('@/components/customer-surface/CustomerAuthModal').then((mod) => mod.CustomerAuthModal),
  { ssr: false },
);

// Reviews (doc 60 §4.2) are no longer imported here: they moved into the branch
// sheet, which already only mounts when a guest opens it — so the island is lazy
// by construction and needs no dynamic() wrapper of its own.
// See components/place/BranchInfoSheet.tsx.
// Customer AI assistant , lazy loaded, only mounts when opened.
//
// The `CUSTOMER_CHAT_ENABLED` constant that used to gate this is gone. It was
// switched off "while the customer surface is being reworked" because the old
// bubble floated above the Place rail and competed with the one primary action
// each screen is meant to have. That rework is what replaced it: the entry is
// now a header pill beside Search and You, and the panel is a sheet or a rail
// rather than something hovering over the guest's order.

const CustomerAssistant = dynamic(
  () => import('@/components/customer-ai/CustomerAssistant').then((mod) => mod.CustomerAssistant),
  { ssr: false },
);
// Scheduled pre-order experience , slot picker + ordering flow.
const PublicMenuScheduledOrderingExperience = dynamic(
  () => import('@/components/menu/PublicMenuScheduledOrderingExperience'),
  { ssr: false },
);
// The guest's allergen/dietary profile editor. Lazy in v2 too: a guest who has
// not asked for it should not pay for the sheet in the menu's first load.
const DietaryPreferencesSheet = dynamic(
  () =>
    import('@/components/menu/DietaryPreferencesSheet').then(
      (mod) => mod.DietaryPreferencesSheet,
    ),
  { ssr: false },
);
// Zone QR picker , shown when a zone_code is present but no table_code yet.
const ZonePickerShell = dynamic(
  () =>
    import('@/components/menu/zone-picker/ZonePickerShell').then(
      (mod) => mod.ZonePickerShell,
    ),
  { ssr: false },
);
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CustomerAccountAffordance } from '@/components/menu/CustomerAccountAffordance';
import { WalletMoment } from '@/components/menu/WalletMoment';
import {
  filterMenuDataForNavigation,
  getMenuCollections as getMenuCollectionViews,
  getNavigationCategories,
} from '@/lib/mnrq/menu-navigation';
import { buildPublicGuestActions, resolveBranchGuestActions } from '@/lib/branch-guest-actions';
import { WEEKDAYS, isBranchOpenNow, normalizeDayShifts, readBranchOperatingHours } from '@/lib/branch-operating-hours';
import { readBranchOrderingSettings } from '@/lib/branch-ordering-settings';
import { getBusinessType, word } from '@/lib/business-types/registry';
import { isBranchItemExcluded, resolveMenuItemServiceState } from '@/lib/menu/availability';
import {
  type PublicPosFeatureFlags,
  type PublicResolvedTableSession,
  readPublicPosFeatureFlags,
  isStoredPublicTableSessionUsable,
  readStoredPublicTableSession,
  readLatestStoredPublicTableSession,
  writeStoredPublicTableSession,
  clearStoredPublicTableSession,
  cleanResolvedScanUrl,
  hasBusinessDisplayData,
  formatClaimExpiry,
  formatServiceRequestLabel,
  formatBranchHoursSummary,
  buildResolvedPublicItems,
  deepMergeMenuData,
  readRememberedCustomerMode,
  rememberCustomerMode,
} from '@/lib/menu/menu-display-helpers';
import { formatPromotionDiscountLabel } from '@/lib/promotions/presentation';
import { isPromotionActive } from '@/lib/promotions/scheduler';
import { beginPublicMenuAnalyticsSession, trackPublicMenuEvent, type PublicMenuAnalyticsSession } from '@/lib/public-menu-analytics';
import { createTimedFetch } from '@/lib/query-resilience';
import { resolveApiRequestUrl } from '@/lib/routing/api-requests';
import type { Promotion, PromotionClaim } from '@/lib/types';
import { toast } from 'sonner';

// Doc 64 §8: the /q/{token} route redirects here with ?qr_error=<CODE> when it
// cannot start a table session. The code is consumed once on mount, mapped to a
// friendly message, and stripped from the URL so a refresh does not re-show it.
// Unknown codes fall back to the UNKNOWN message.
const QR_ERROR_MESSAGES: Record<string, string> = {
  BRANCH_CLOSED: 'This branch is currently closed. Please try again during operating hours.',
  SUBSCRIPTION_INACTIVE: 'This branch does not have an active subscription.',
  SHIFT_CLOSED: 'Orders cannot be placed right now.',
  TABLE_RESERVED: 'This table is reserved. Please check in with a host.',
  BRANCH_INACTIVE: 'This branch is currently inactive.',
  UNKNOWN: 'We could not start a table session for this code.',
};

export default function MenuDisplayPage({
  initialMenu,
  experienceV2 = false,
  siblingBranchCount = 0,
  initialCart = null,
  reorderNotice = null,
  initialOpenItem = null,
  lineTabs = [],
  activeLineSlug = null,
  activeLineId = null,
  cartLines = [],
  initialMenuView = null,
  menuViewPreferenceCookie,
}: {
  initialMenu: any;
  /** Enables the QR-first customer web presentation while keeping the ordering engine shared. */
  experienceV2?: boolean;
  /**
   * How many active branches this business has (doc 70 F2). Anything above 1
   * means "order from a different location" has somewhere to go; at 1 the
   * control is not rendered rather than rendered as a link that reloads the
   * same menu.
   */
  siblingBranchCount?: number;
  /**
   * The body the guest asked for (`?view=`), resolved on the server. Null means
   * "no opinion" — the merchant's own template wins.
   */
  initialMenuView?: PlaceMenuViewMode | null;
  /** Branch-scoped cookie name used to restore a guest's last view on return. */
  menuViewPreferenceCookie?: string;
  /** A previous order's cart, rebuilt server-side (doc 54 / C2 "Order again"). */
  initialCart?: CartItem[] | null;
  /** One-line context for the guest when the cart came from a prefill link
   *  (reorder doc 54 / C2, or doc 59 F3 "your usual"). `label` overrides the
   *  success prefix; `emptyLabel` overrides the couldn't-rebuild toast. */
  reorderNotice?: {
    orderNumber: string | null;
    skippedCount: number;
    cartEmpty?: boolean;
    label?: string;
    emptyLabel?: string;
  } | null;
  /**
   * Open one item's sheet on arrival, instead of prefilling the cart.
   *
   * Used by single-item reorder ("your usual") when the item has a required
   * choice — a size or a side that has no honest default. Dropping such an item
   * straight into the cart would invent a selection the guest never made, so the
   * sheet is opened for them to make it.
   */
  initialOpenItem?: OrderingMenuItem | null;
  /**
   * Doc 03 line tabs. Empty for a single-line branch, which is every branch today —
   * `PlaceLineTabs` renders nothing below two, so this is a no-op until a merchant
   * adds a second line.
   */
  lineTabs?: PlaceLineTab[];
  activeLineSlug?: string | null;
  /** Stamped onto every cart item the guest adds while this line is on screen. */
  activeLineId?: string | null;
  /** Line metadata for the cart bar's per-line breakdown. */
  cartLines?: { id: string; name: string; kind: MenuLineKind }[];
}) {
  // The MENU's own slug, taken from the server-resolved data rather than the URL.
  //
  // Under the old address the route param *was* the menu slug, so `useParams().slug`
  // happened to be right. After doc 02 the URL carries a business and a branch, and
  // reading `slug` off params silently yielded `undefined` — which broke translation,
  // the refetch, the realtime filter and analytics all at once, with no error until
  // something called `.trim()` on it. It is data, so it comes from the data.
  const { business: businessSegment, branch: branchSegment } = useParams<{
    business?: string;
    branch?: string;
  }>();
  const slug = initialMenu?.slug ?? '';
  const searchParams = useSearchParams();

  const [business, setBusiness] = useState<any | null>(initialMenu.business || null);
  const [menu, setMenu] = useState<any>(initialMenu);
  const [branch, setBranch] = useState<any | null>(initialMenu.branch || initialMenu.menu?.branch || null);
  const [categories, setCategories] = useState<any[]>(initialMenu.categories || []);
  const [collections, setCollections] = useState<any[]>(initialMenu.collections || []);
  const [items, setItems] = useState<any[]>(initialMenu.menu_items || []);
  const [promotions, setPromotions] = useState<any[]>(initialMenu.promotions || []);
  const [branchPosAddonActive, setBranchPosAddonActive] = useState(Boolean(initialMenu.branchPosAddonActive));
  const [branchPosFeatures, setBranchPosFeatures] = useState<PublicPosFeatureFlags>(() => readPublicPosFeatureFlags(initialMenu));
  const [template, setTemplate] = useState<TemplateRecord | null>(initialMenu.template || null);
  const [isHydrating, setIsHydrating] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [claimingPromotionId, setClaimingPromotionId] = useState<string | null>(null);
  const [claimResult, setClaimResult] = useState<{ claim: PromotionClaim; reused: boolean } | null>(null);
  const [tableSession, setTableSession] = useState<PublicResolvedTableSession | null>(null);
  const [isResolvingTableSession, setIsResolvingTableSession] = useState(false);
  const [tableSessionError, setTableSessionError] = useState<string | null>(null);
  // Doc 51 §3.3: the /q/{token} route hands a session off in an HttpOnly cookie
  // with no ?table_code= in the URL, so it is read once here via the current
  // endpoint and stored like any other resolved session.
  //
  // A REF, not state: the flag is only read inside the effect, and a state
  // update here would re-run the effect (it is in the deps), which fires the
  // previous run's cleanup (`active = false`) and discards the in-flight
  // /current fetch — the session would never land in state and the guest would
  // sit in browse mode after a successful QR scan.
  //
  // `lastScanSignatureRef` remembers which scan context the flag belongs to.
  // When the context changes (a fresh ?qrcode=true scan, a new table_code, a
  // different menu) the flag resets so the new cookie's session is read —
  // otherwise a guest who scans a second QR after navigating away would keep
  // the first session (or none) instead of the new one.
  const cookieSessionCheckedRef = useRef(false);
  const lastScanSignatureRef = useRef('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  /** The Chow auth modal. V2 only: V1's sign-in paths live inside its own sheets. */
  const [authOpen, setAuthOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [activeServiceRequest, setActiveServiceRequest] = useState<string | null>(null);
  // Menu body, place chrome, cart and checkout follow the guest preference as
  // one branch-scoped palette instead of pinning the ordering island to Chow's
  // default neutrals.
  const { theme: publicTheme } = useTheme();
  const publicThemeMode = publicTheme === 'dark' ? 'dark' : 'light';
  // True while the ordering mini-cart sheet is expanded. Page-level chrome that
  // floats above it (the chat bubble) hides while it is open.
  const [orderingCartOpen, setOrderingCartOpen] = useState(false);
  // True while the full-page delivery checkout is open. The checkout closes the
  // cart sheet before opening, so without this the shell's cart bar would
  // reappear on top of the checkout footer — suppress it too.
  const [deliveryCheckoutOpen, setDeliveryCheckoutOpen] = useState(false);
  // The checkout replaces the whole shell, so the page height collapses to one
  // viewport when it opens. If the guest was scrolled down the menu, the
  // browser clamps the scroll mid-surface — snap back to the top so the
  // checkout header is what they see.
  useEffect(() => {
    if (deliveryCheckoutOpen) window.scrollTo(0, 0);
  }, [deliveryCheckoutOpen]);
  // Search and account are header controls in the Place shell rather than
  // collapsible panels, so the page owns their open state.
  const [searchOpen, setSearchOpen] = useState(false);
  // V2 puts the dietary filter in the menu itself, so the page (not the
  // ordering island) owns the profile and the guest's hide switch. The hook is
  // safe to mount twice: its account→device restore is one-shot per document.
  const { profile: dietaryProfile } = useDietaryProfileSync();
  const [hideDietaryClashes, setHideDietaryClashes] = useState(false);
  const [dietaryPrefsOpen, setDietaryPrefsOpen] = useState(false);
  const hasDietaryProfile = !isDietaryProfileEmpty(dietaryProfile);
  useEffect(() => {
    if (!hasDietaryProfile && hideDietaryClashes) setHideDietaryClashes(false);
  }, [hasDietaryProfile, hideDietaryClashes]);
  const [accountOpen, setAccountOpen] = useState(false);
  const [customerMode, setCustomerMode] = useState<PublicMenuMode['key']>('menu');
  /**
   * Has the guest answered "how do you want this?" yet?
   *
   * WHAT THIS REPLACES. Two separate first-run interruptions used to fight over
   * this question: a modal arrival sheet listing every mode, and a "Browse" chip
   * that was selected by default and meant "you cannot order". A guest who
   * dismissed the modal — most of them — was left in Browse, where every dish
   * ended at "Scan the QR code at your table to order this" even on a branch
   * that would happily have done pickup.
   *
   * Now the page picks the best real mode up front so the menu is orderable
   * from the first tap, and this flag only governs whether the ACTION BAR still
   * needs to confirm the choice. It is asked once, when the first item lands in
   * the cart, with the food already chosen and the consequence real. A branch
   * offering one way to get food never asks at all.
   *
   * Pre-answered for a reorder (doc 54 / C2): that link carries a definite
   * intent and the guest is one tap from checkout.
   */
  const [fulfilmentAnswered, setFulfilmentAnswered] = useState(Boolean(initialCart?.length));
  /**
   * Guards the one-time reorder notice toast (doc 54 / C2). Fired once per page
   * load when the cart was prefilled from a previous order.
   */
  const reorderNoticeShownRef = React.useRef(false);
  /**
   * Guards the one-shot restore of a previously chosen ordering mode. Without
   * persistence, picking "Delivery" and then reloading (or coming back via the
   * browser's back button) dropped the guest to plain browse mode and they had
   * to find the picker again.
   *
   * sessionStorage, not localStorage, on purpose: the choice should survive a
   * reload and in-tab navigation, but a guest who ordered delivery last week
   * should not silently reopen the menu locked into delivery.
   */
  const modeRestoredRef = React.useRef(false);
  const sessionModeAppliedRef = React.useRef<string | null>(null);
  const [nextOpenScheduledTime, setNextOpenScheduledTime] = useState<string | null>(null);
  const [isSchedulingForNextOpen, setIsSchedulingForNextOpen] = useState(false);
  const [nextOpenBannerError, setNextOpenBannerError] = useState<string | null>(null);
  const [browseDetailItem, setBrowseDetailItem] = useState<OrderingMenuItem | null>(null);
  const [browseDetailOpen, setBrowseDetailOpen] = useState(false);
  /**
   * Item handed to the ordering surface when a browse-mode tap routes into an
   * available ordering mode (see the browse click handler below). Cleared once
   * the ordering experience has opened the item's sheet.
   */
  // Seeded from `initialOpenItem` so a single-item reorder link opens that
  // item's sheet on first paint, with no flash of the plain menu first.
  const [browseOrderItem, setBrowseOrderItem] = useState<OrderingMenuItem | null>(initialOpenItem);

  // A ribbon cart tap in browse mode: there is no cart sheet to open until an
  // ordering surface exists, so the tap switches into the best available
  // ordering mode and flags the pending cart. The ordering experience consumes
  // the flag on mount (`initialCartOpen`), then we clear it.
  const [pendingCartOpen, setPendingCartOpen] = useState(false);
  // Set together with `pendingCartOpen` when the switch happens FROM checkout,
  // so the new mode's island opens on the step the guest was already on.
  const [pendingCheckoutOpen, setPendingCheckoutOpen] = useState(false);

  const browseItemsById = useMemo(() => {
    const map = new Map<string, OrderingMenuItem>();
    for (const item of items) {
      map.set(String(item.id), item as OrderingMenuItem);
    }
    return map;
  }, [items]);

  const isBrowseMode = customerMode === 'menu';

  /* ── The row's interactive contract (doc 08 phase 4.1) ──
   *
   * Mounted here rather than inside the ordering island, because the island
   * only exists once a fulfilment mode is chosen. A browse-only menu still has
   * rows a guest must be able to see, reach and activate — and until now its
   * dishes were announced as nothing and were unreachable by keyboard.
   *
   * `browseItemsById` is the full item set in every mode, so it is the right
   * source for the accessible names. */
  const orderableRowLabels = useMemo(
    () => ({ get: (itemId: string) => browseItemsById.get(itemId)?.title }),
    [browseItemsById],
  );
  useOrderableMenuRows({
    rootSelector: MENU_BODY_SELECTOR,
    labels: orderableRowLabels,
  });

  // The browse-mode item tap handler lives with `customerModes` further down:
  // whether a tap routes into an ordering surface (or opens the read-only
  // detail sheet) depends on the modes the branch actually offers.
  const [activeOperatingModes, setActiveOperatingModes] = useState<Partial<Record<OperatingMode, boolean>>>(
    () => (initialMenu?.operatingModes || {}) as Partial<Record<OperatingMode, boolean>>,
  );
  const trackedEventsRef = useRef<Set<string>>(new Set());
  const analyticsSessionRef = useRef<PublicMenuAnalyticsSession | null>(null);
  const realtimeReloadTimerRef = useRef<number | null>(null);
  const { containerRef: pullRef, state: pullState, pullDistance } = usePullToRefresh<HTMLDivElement>(
    () => setReloadNonce((n) => n + 1),
  );

  useMenuItemImpressionTracker(pullRef, menu?.id);

  const lastSearchTermRef = useRef<string>('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback(
    (term: string) => {
      setSearchQuery(term);
      const trimmed = term.trim();
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (trimmed.length < 2) {
        lastSearchTermRef.current = '';
        return;
      }
      searchTimerRef.current = setTimeout(() => {
        if (lastSearchTermRef.current === trimmed) return;
        lastSearchTermRef.current = trimmed;
        const q = trimmed.toLowerCase();
        const resultCount = (items || []).filter((it) => matchesMenuQuery(it, q)).length;
        if (menu?.id) trackSearchPerformed(menu.id, { term: trimmed, resultCount });
      }, 500);
    },
    [items, menu?.id],
  );
  const timedFetch = useMemo(() => createTimedFetch(), []);
  const contentMenuId = menu?.content_menu_id || menu?.id || null;
  // The customer's session lives host-scoped on the customer surface; read it
  // from there (same source as the account panel) rather than the menu host's
  // next-auth context, whose server prop can lag a sign-in that happened
  // in-place until a reload or the 5-minute refetch interval.
  const { data: customerSession, status: customerSessionStatus, refresh: refreshCustomerSession } = useCustomerSession();
  const customer = customerSession?.user as
    | { name?: string | null; email?: string | null; phoneNumber?: string | null }
    | undefined;
  const customerSignedIn = customerSessionStatus === 'authenticated' && Boolean(customer);
  const customerContact = customer?.phoneNumber?.trim() || customer?.email?.trim() || '';
  /**
   * An account signed up with email OTP alone carries no `name`, and an empty
   * name here made the counter/self-service surface prompt "what's your name?"
   * to someone who was signed in — every order, forever. The local part of the
   * verified email is a real, stable handle for that person; the merchant can
   * see the full contact on the ticket. Only signed-in customers get the
   * fallback, so a true guest is still asked, which is correct.
   */
  const customerDisplayName =
    customer?.name?.trim() ||
    (customerSignedIn
      ? customerContact.includes('@')
        ? customerContact.split('@')[0]
        : customerContact
      : '');

  // Doc 60 §5.3: signed-out guests may leave private table feedback too. When
  // not signed in, attach the device handle so the merchant can follow up on a
  // note across the guest's own orders -- it is never shown publicly.
  const guestUsernameForFeedback = customerSignedIn ? null : getGuestUsername();

  // Refresh the session the moment the account panel completes a sign-in, so
  // the ordering surface (and its loyalty panel) reflects the signed-in state
  // immediately -- no reload required.
  useEffect(() => {
    const handler = () => {
      void refreshCustomerSession();
    };
    window.addEventListener('mnrq:customer-session-changed', handler);
    return () => window.removeEventListener('mnrq:customer-session-changed', handler);
  }, [refreshCustomerSession]);


  const {
    selectedLanguage, setSelectedLanguage, translatedData, isTranslating, translate,
  } = useMenuTranslation(slug as string);

  const queueMenuReload = useCallback(() => {
    if (realtimeReloadTimerRef.current !== null) return;
    realtimeReloadTimerRef.current = window.setTimeout(() => {
      realtimeReloadTimerRef.current = null;
      setReloadNonce((nonce) => nonce + 1);
    }, 100);
  }, []);

  useEffect(() => {
    return () => {
      if (realtimeReloadTimerRef.current) {
        window.clearTimeout(realtimeReloadTimerRef.current);
      }
    };
  }, []);

  const brandingLevel = useMemo<BrandingLevel>(() => {
    const normalized = String(initialMenu?.brandingLevel || 'free').toLowerCase();
    if (normalized === 'free') return 'free';
    return 'none';
  }, [initialMenu?.brandingLevel]);

  const branchOrderingSettings = useMemo(
    () => readBranchOrderingSettings(branch),
    [branch],
  );
  const isCounterPickupSession = useMemo(() => {
    if (tableSession?.sessionType === 'counter' || tableSession?.qrKind === 'counter') {
      return true;
    }
    const serviceAreaKind = String(tableSession?.table.service_area_kind || '').trim().toLowerCase();
    const orderFlow = String(tableSession?.table.order_flow || '').trim().toLowerCase();
    return Boolean(
      tableSession?.table.is_counter_pickup ||
      serviceAreaKind === 'pickup_counter' ||
      serviceAreaKind === 'counter_pickup' ||
      orderFlow === 'counter_pickup' ||
      orderFlow === 'takeout_pickup',
    );
  }, [tableSession]);
  const branchOrderingEnabled = useMemo(
    () => {
      if (!branchPosFeatures.workspace || !branchPosFeatures.guestOrdering) return false;
      if (isCounterPickupSession) {
        // Doc 70 F10: counter pickup is gated by the operating-mode registry,
        // not the legacy `counter_pickup_ordering_enabled` column (defaults
        // false, never backfilled — 37/39 pickup-enabled branches silently
        // lost counter ordering). The registry is resolved server-side and
        // shipped in `initialMenu.operatingModes`.
        return activeOperatingModes.counter_pickup === true && branchPosFeatures.counterQr;
      }
      return branchOrderingSettings.tableOrderingEnabled;
    },
    [activeOperatingModes, branchOrderingSettings, branchPosFeatures, isCounterPickupSession],
  );
  const serviceRequestsEnabled = Boolean(
    branchPosFeatures.workspace &&
      branchPosFeatures.serviceRequests &&
      tableSession &&
      !isCounterPickupSession,
  );

  // Delivery map picker start point: the branch's current location pin (food
  // trucks move), falling back to nothing so the picker just opens on the
  // guest's GPS or a neutral point.
  const branchLocation = useMemo(() => {
    const rawLat = branch?.current_location_lat;
    const rawLng = branch?.current_location_lng;
    if (rawLat == null || rawLng == null) return null;
    const lat = Number(rawLat);
    const lng = Number(rawLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat,
      lng,
      label: branch?.current_location_label ?? branch?.address ?? branch?.name ?? null,
    };
  }, [branch]);

  const loadMenuData = useCallback(async (forceRefresh: boolean) => {
      const useHydratedSnapshot =
        !forceRefresh &&
        Boolean(initialMenu?.id) &&
        String(initialMenu?.slug || slug || '') === String(slug || initialMenu?.slug || '');

      if (useHydratedSnapshot) {
        const hydratedMenu = initialMenu;
        const contentMenuId = hydratedMenu.content_menu_id || hydratedMenu.id;
        const hydratedBranch = hydratedMenu.branch || hydratedMenu.menu?.branch || null;
        const availabilityTimezone = hydratedBranch?.timezone || 'UTC';
        const [fetchedAvailability, fetchedServiceOverrides] = await Promise.all([
          contentMenuId
            ? getMenuItemAvailabilityData(contentMenuId)
            : Promise.resolve({ rulesByItemId: new Map(), exceptionsByItemId: new Map() }),
          contentMenuId
            ? getBranchMenuItemServiceOverrides({
                branchId: hydratedMenu.branch_id || null,
                menuId: contentMenuId,
              }).catch(() => new Map())
            : Promise.resolve(new Map()),
        ]);

        return {
          business: hydratedMenu.business || null,
          menu: hydratedMenu,
          branch: hydratedBranch,
          categories: Array.isArray(hydratedMenu.categories) ? hydratedMenu.categories : [],
          collections: Array.isArray(hydratedMenu.collections) ? hydratedMenu.collections : [],
          items: buildResolvedPublicItems({
            items: Array.isArray(hydratedMenu.menu_items) ? hydratedMenu.menu_items : [],
            availability: fetchedAvailability,
            serviceOverrides: fetchedServiceOverrides,
            timezone: availabilityTimezone,
          }),
          promotions: Array.isArray(hydratedMenu.promotions)
            ? hydratedMenu.promotions.filter((promo: Promotion) => isPromotionActive(promo))
            : [],
          template: (hydratedMenu.template as TemplateRecord | null) || null,
          branchPosFeatures: readPublicPosFeatureFlags(hydratedMenu),
        };
      }

      const fetchedMenu = slug ? await getMenuBySlug(slug) : initialMenu;
      if (!fetchedMenu) {
        return null;
      }

      const contentMenuId = fetchedMenu.content_menu_id || fetchedMenu.id;
      const [fetchedBusiness, fetchedCategories, fetchedCollections, fetchedItems, fetchedOptionGroups, fetchedAvailability, fetchedPromotions, fetchedTemplate, fetchedBranch] = await Promise.all([
        fetchedMenu.business_id ? getBusinessById(fetchedMenu.business_id) : Promise.resolve(null),
        contentMenuId ? getMenuCategories(contentMenuId) : Promise.resolve([]),
        contentMenuId ? getMenuCollections(contentMenuId) : Promise.resolve([]),
        contentMenuId ? getMenuItems(contentMenuId) : Promise.resolve([]),
        contentMenuId ? getMenuItemOptionGroups(contentMenuId) : Promise.resolve([]),
        contentMenuId ? getMenuItemAvailabilityData(contentMenuId) : Promise.resolve({ rulesByItemId: new Map(), exceptionsByItemId: new Map() }),
        fetchedMenu.id ? getPromotions(fetchedMenu.id) : Promise.resolve([]),
        fetchedMenu.template_id ? getTemplate(fetchedMenu.template_id) : Promise.resolve(null),
        fetchedMenu.branch_id ? getBranchById(fetchedMenu.branch_id) : Promise.resolve(null),
      ]);

      // Layered branch sharing (doc 38 Part 2): a linked branch's catalogue is
      // the content source PLUS its own local overlay. This client rebuild read
      // only the content source, so whenever it ran instead of the SSR snapshot
      // the branch's local items vanished from the menu mid-session. Mirrors
      // `loadPublicMenuPageData`, which unions the same two menu ids.
      const isLinkedMenu = Boolean(fetchedMenu.content_menu_id && fetchedMenu.content_menu_id !== fetchedMenu.id);
      const [localItems, localOptionGroups] = isLinkedMenu
        ? await Promise.all([
            getMenuItems(fetchedMenu.id).catch(() => []),
            getMenuItemOptionGroups(fetchedMenu.id).catch(() => []),
          ])
        : [[], []];

      const optionGroupsByItemId = new Map<string, any[]>();
      for (const group of [
        ...(Array.isArray(fetchedOptionGroups) ? fetchedOptionGroups : []),
        ...(Array.isArray(localOptionGroups) ? localOptionGroups : []),
      ]) {
        const existing = optionGroupsByItemId.get(group.menu_item_id) || [];
        existing.push(group);
        optionGroupsByItemId.set(group.menu_item_id, existing);
      }

      const fetchedServiceOverrides = contentMenuId
        ? await getBranchMenuItemServiceOverrides({
            branchId: fetchedMenu.branch_id || null,
            menuId: contentMenuId,
          }).catch(() => new Map())
        : new Map();

      const availabilityTimezone = fetchedBranch?.timezone || 'UTC';
      let nextTemplate: TemplateRecord | null = null;
      let nextMenu = fetchedMenu;
      if (fetchedTemplate && Array.isArray(fetchedTemplate) && fetchedTemplate.length > 0) {
        nextTemplate = fetchedTemplate[0] as TemplateRecord;
      } else if (fetchedTemplate && !Array.isArray(fetchedTemplate)) {
        nextTemplate = fetchedTemplate as TemplateRecord;
      }

      if (fetchedMenu?.id) {
        try {
          const seasonalResponse = await timedFetch(resolveApiRequestUrl(`/api/seasonal-rules/resolve?menuId=${fetchedMenu.id}`), {
            cache: 'no-store',
          });
          const seasonalPayload = await seasonalResponse.json().catch(() => null);
          if (seasonalResponse.ok && seasonalPayload?.template) {
            nextTemplate = seasonalPayload.template as TemplateRecord;
          }
          if (seasonalResponse.ok && seasonalPayload?.menuDesign) {
            const resolvedDesign = seasonalPayload.menuDesign as { themeId?: string | null; designOverrides?: Record<string, unknown> | null };
            nextMenu = {
              ...fetchedMenu,
              theme_id: resolvedDesign.themeId ?? null,
              design_overrides: resolvedDesign.designOverrides ?? null,
            };
          }
        } catch {
          // fallback to base template
        }
      }

      return {
        business: fetchedBusiness,
        menu: nextMenu,
        branch: fetchedBranch || null,
        categories: Array.isArray(fetchedCategories) ? fetchedCategories : [],
        collections: Array.isArray(fetchedCollections) ? fetchedCollections : [],
        items: buildResolvedPublicItems({
          items: [
            ...(Array.isArray(fetchedItems) ? fetchedItems : []),
            ...(Array.isArray(localItems) ? localItems : []),
          ].map((item) => ({
            ...item,
            // Mirrors `loadPublicMenuPageData`: prices are formatted from
            // `item.currency`, which only the menu row carries.
            currency: (item as { currency?: string }).currency ?? fetchedMenu.currency ?? undefined,
            option_groups: optionGroupsByItemId.get(item.id) || [],
          })),
          availability: fetchedAvailability,
          serviceOverrides: fetchedServiceOverrides,
          timezone: availabilityTimezone,
        }),
        promotions: Array.isArray(fetchedPromotions) ? fetchedPromotions.filter((promo) => isPromotionActive(promo)) : [],
        template: nextTemplate,
        branchPosFeatures: readPublicPosFeatureFlags(fetchedMenu?.branchPosFeatures ? fetchedMenu : initialMenu),
      };
    }, [initialMenu, slug, timedFetch]);

  useEffect(() => {
    let active = true;

    const fetchAllData = async () => {
      setIsHydrating(true);
      setLoadError(null);

      try {
        const resolvedSnapshot = await loadMenuData(reloadNonce > 0);
        if (!active || !resolvedSnapshot) {
          if (active && !resolvedSnapshot) {
            setLoadError(new Error('We could not find this menu right now.'));
          }
          return;
        }

        setMenu((prev: Record<string, unknown> | null) => ({ ...(prev || {}), ...resolvedSnapshot.menu }));
        setBusiness((prev: Record<string, unknown> | null) => {
          if (!hasBusinessDisplayData(resolvedSnapshot.business)) {
            return prev || null;
          }
          return { ...(prev || {}), ...resolvedSnapshot.business };
        });
        setCategories(resolvedSnapshot.categories);
        setCollections(resolvedSnapshot.collections);
        setItems(resolvedSnapshot.items);
        setPromotions(resolvedSnapshot.promotions);
        setBranch(resolvedSnapshot.branch);
        setBranchPosAddonActive(Boolean(resolvedSnapshot.branchPosFeatures.workspace));
        setBranchPosFeatures(resolvedSnapshot.branchPosFeatures);

        if (resolvedSnapshot.template) {
          setTemplate(resolvedSnapshot.template);
        } else if (!resolvedSnapshot.menu.template_id) {
          setTemplate(null);
        }
      } catch {
        if (active) {
          setLoadError(new Error('We could not refresh this menu right now.'));
        }
      } finally {
        if (active) {
          setIsHydrating(false);
        }
      }
    };

    void fetchAllData();
    return () => {
      active = false;
    };
  }, [loadMenuData, reloadNonce]);

  const menuData = useMemo(() => {
    if (!menu) return undefined;

    // Merge categories that share the same display name (case-insensitive) so
    // duplicate-name categories created in the management UI don't render as
    // separate sections on the customer-facing menu.
    // When duplicate names are detected, all items are consolidated under the
    // first category with that name , subsequent duplicate categories' items
    // are silently merged rather than lost.
    const rawCategories = categories || [];
    const mergedById = new Map<string, typeof rawCategories[0]>();
    const canonicalIdByName = new Map<string, string>();
    rawCategories.forEach((cat) => {
      const key = (cat.name || '').toLowerCase().trim();
      if (!canonicalIdByName.has(key)) {
        canonicalIdByName.set(key, cat.id);
        mergedById.set(cat.id, cat);
      }
    });
    const mergedCategories = Array.from(mergedById.values());
    const itemsByCategory = Object.fromEntries(
      mergedCategories.map((cat) => {
        const key = (cat.name || '').toLowerCase().trim();
        const idsForName = rawCategories
          .filter((c) => (c.name || '').toLowerCase().trim() === key)
          .map((c) => c.id);
        const allItems = (items || []).filter((item) => idsForName.includes(item.category_id));
        const query = (searchQuery || '').trim().toLowerCase();
        const matched = query
          ? allItems.filter((item) => matchesMenuQuery(item, query))
          : allItems;
        // Deduplicate items by title within merged category group
        const seenTitles = new Set<string>();
        const deduped = matched.filter((item) => {
          const t = (item.title || '').toLowerCase().trim();
          if (seenTitles.has(t)) return false;
          seenTitles.add(t);
          return true;
        });
        return [cat.id, deduped];
      }),
    );

    return buildMenuData({
      business: business || null,
      menu,
      branch,
      categories: mergedCategories,
      collections: collections || [],
      itemsByCategory,
      promotions,
    });
    }, [business, menu, branch, categories, collections, items, promotions, searchQuery]);

  const effectiveMenuData = useMemo(() => {
    if (!menuData) return undefined;
    if (!translatedData || isOriginalLanguage(selectedLanguage)) return menuData;
    return deepMergeMenuData(menuData, translatedData as any);
  }, [menuData, translatedData, selectedLanguage]);

  /**
   * Item titles as the guest is currently reading them.
   *
   * `effectiveMenuData` is the merge of the menu with whatever translation is
   * active, so this is the only place that knows a dish is showing as "Spring
   * Rolls" rather than "Rouleaux Printaniers". The assistant reads titles from
   * the database and would otherwise name dishes that are not on screen under
   * that name.
   */
  const assistantItemTitle = useMemo(() => {
    const byId = new Map<string, string>();
    // `buildMenuData` nests these under `menu`, not at the top level. Reading
    // `effectiveMenuData.categories` silently produced an empty map, so the
    // lookup always fell through to the untranslated title and every renderer
    // wired to it kept showing the old name — a fix that type-checked, ran, and
    // did nothing.
    const categories = (effectiveMenuData as {
      menu?: { categories?: Array<{ items?: Array<{ id?: unknown; title?: unknown }> }> };
    } | undefined)?.menu?.categories ?? [];
    for (const category of categories) {
      for (const item of category.items ?? []) {
        if (item?.id && typeof item.title === 'string') byId.set(String(item.id), item.title);
      }
    }
    return (id: string, fallback: string) => byId.get(id) ?? fallback;
  }, [effectiveMenuData]);

  const collectionViews = useMemo(() => getMenuCollectionViews(effectiveMenuData), [effectiveMenuData]);
  const navigationCategories = useMemo(
    () => getNavigationCategories(effectiveMenuData, activeCollectionId),
    [effectiveMenuData, activeCollectionId],
  );

  useEffect(() => {
    if (collectionViews.length === 0) {
      setActiveCollectionId(null);
      return;
    }

    const stillExists = activeCollectionId
      ? collectionViews.some((collection) => collection.id === activeCollectionId)
      : true;

    if (!stillExists) {
      const defaultCollection = collectionViews.find((collection) => collection.is_default) || collectionViews[0];
      setActiveCollectionId(defaultCollection?.id || null);
    }
  }, [collectionViews, activeCollectionId]);

  useEffect(() => {
    if (!activeCategoryId) return;
    const stillExists = navigationCategories.some((category) => category.id === activeCategoryId);
    if (!stillExists) {
      setActiveCategoryId(null);
    }
  }, [navigationCategories, activeCategoryId]);

  const presentedMenuData = useMemo(() => {
    if (!effectiveMenuData) return undefined;
    return filterMenuDataForNavigation(effectiveMenuData, activeCollectionId, activeCategoryId);
  }, [effectiveMenuData, activeCollectionId, activeCategoryId]);

  /**
   * The menu index the voice resolver matches against (doc 64 §9).
   *
   * Built from the SAME items the menu renders, so a resolver hit names a dish
   * the guest can actually see. `presentedMenuData` is the category-filtered
   * slice the guest is looking at; `effectiveMenuData` is the full translated
   * menu, used when the presented slice is empty. When the index is null/empty
   * the resolver simply falls through to the model — correct behaviour, not an
   * error.
   *
   * Mapped defensively: `price` may be a number or a numeric string, titles
   * may live in `title` or `name`, and `category_id` rides on the raw row
   * rather than the `OrderingMenuItem` type.
   */
  const assistantMenuIndex = useMemo<ResolverMenuIndex | null>(() => {
    const items = (presentedMenuData?.menu?.items || effectiveMenuData?.menu?.items || []) as OrderingMenuItem[];
    if (items.length === 0) return null;
    return {
      items: items.map((item) => ({
        id: String(item.id),
        title: item.title ?? item.name ?? '',
        price: typeof item.price === 'number' ? item.price : Number(item.price ?? item.price_value ?? 0) || 0,
        categoryId: (item as { category_id?: string | null }).category_id ?? null,
        available: item.available ?? item.available_for_order,
      })),
      categories: (
        (presentedMenuData?.menu?.categories || effectiveMenuData?.menu?.categories || []) as Array<{
          id: string;
          name?: string;
        }>
      ).map((category) => ({ id: String(category.id), name: category.name || '' })),
    };
    // Keyed on the items array identity: the index rebuilds only when the menu
    // the page renders actually changes.
  }, [presentedMenuData?.menu?.items, effectiveMenuData?.menu?.items]);

  const prevLanguageRef = useRef(selectedLanguage);
  useEffect(() => {
    if (!menuData || isOriginalLanguage(selectedLanguage)) return;
    if (prevLanguageRef.current !== selectedLanguage || !translatedData) {
      translate(menuData as unknown as Record<string, unknown>, selectedLanguage);
      prevLanguageRef.current = selectedLanguage;
    }
  }, [selectedLanguage, menuData, translate, translatedData]);

  const resolvedTemplateSchema = useMemo<Template>(() => {
    const schema = ((template?.json_schema as Template | undefined) || {} as Template);
    const overrides = (menu?.design_overrides ?? {}) as import('@/lib/menu-themes/design-overrides').DesignOverrides;
    if (!overrides?.colors && !overrides?.fonts && !overrides?.skin && !overrides?.templateColors && !overrides?.templateFonts) return schema;
    return applyDesignOverrides(schema, overrides);
  }, [template, menu]);

  // A template is "valid" when its schema carries an engine (storefront,
  // paged-flip), paged content (`pagedFlip`/`pagedNodes`/`pages`), or legacy
  // renderable nodes. The null fallback (platform-default) has none of those,
  // so it must NOT route through a template engine — see the `templateEngine`
  // gate below. Requiring engine/paged here alone would wrongly clear the 89
  // built-in legacy-scroll templates that declare no engine at all.
  const hasValidTemplate = useMemo(() => {
    const t = resolvedTemplateSchema;
    const nodes = (t as { nodes?: unknown } | undefined)?.nodes;
    return Boolean(
      t?.engine || t?.pagedFlip || t?.pagedNodes || (Array.isArray(nodes) && nodes.length > 0),
    );
  }, [resolvedTemplateSchema]);

  // Three families, three renderers — restored so a flipbook template shows its
  // book, a legacy template shows its board, and only true storefront / default
  // menus wear Menuraq's own body. The merchant's engine choice wins.
  const templateEngine = hasValidTemplate ? resolveTemplateEngine(resolvedTemplateSchema) : 'legacy-scroll';

  /**
   * The body on screen — Menuraq's own menu body, or the template's own body.
   * The rules live in `lib/customer/place-menu-view.ts`; the guest's choice
   * arrives as `?view=` and is resolved on the server, so the body is right in
   * the FIRST paint.
   */
  const hasRenderableTemplateBody = useMemo(
    () => hasRenderableTemplateBodyOf(resolvedTemplateSchema),
    [resolvedTemplateSchema],
  );
  const resolvedMenuView = useMemo(
    () => resolvePlaceMenuView({
      requested: initialMenuView,
      hasRenderableBody: hasRenderableTemplateBody,
      engine: templateEngine,
    }),
    [initialMenuView, hasRenderableTemplateBody, templateEngine],
  );
  const [activeView, setActiveView] = useState<PlaceMenuViewMode>(resolvedMenuView.activeView);
  const showTemplateBody = activeView === 'design' && hasRenderableTemplateBody;

  // The Chow app switches two already-loaded menu bodies in place. Keep the
  // same fast interaction here, while recording each choice in browser history
  // so copied URLs and Back/Forward still restore the selected body.
  useEffect(() => {
    setActiveView(resolvedMenuView.activeView);
  }, [resolvedMenuView.activeView]);

  useEffect(() => {
    if (!experienceV2) return;
    const restoreFromUrl = () => {
      const requested = parsePlaceMenuViewMode(new URL(window.location.href).searchParams.get('view'));
      setActiveView(resolvePlaceMenuView({
        requested,
        hasRenderableBody: hasRenderableTemplateBody,
        engine: templateEngine,
      }).activeView);
    };
    window.addEventListener('popstate', restoreFromUrl);
    return () => window.removeEventListener('popstate', restoreFromUrl);
  }, [experienceV2, hasRenderableTemplateBody, templateEngine]);

  const changeMenuView = useCallback((nextView: PlaceMenuViewMode) => {
    if (nextView === activeView) return;
    const href = buildPlaceMenuViewHref(window.location.pathname, window.location.search, { view: nextView });
    window.history.pushState(null, '', href);
    if (menuViewPreferenceCookie) {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${menuViewPreferenceCookie}=${nextView}; Path=/m; Max-Age=31536000; SameSite=Lax${secure}`;
    }
    setActiveView(nextView);
  }, [activeView, menuViewPreferenceCookie]);
  // Mirrors `resolvePlaceMenuView`'s own gate: a storefront template IS the
  // platform list, so switching to it would show the same body twice. The
  // switcher and the V2 mode note both need this, not the raw body flag.
  const canShowDesign = hasRenderableTemplateBody && templateEngine !== 'storefront';

  const hasDualThemeVariants = Boolean(
    resolvedTemplateSchema?.themeVariants?.light &&
      resolvedTemplateSchema?.themeVariants?.dark,
  );

  // One broadcast signal per menu/branch/template instead of seven postgres_changes
  // channels (six of which were never published). See useMenuChangeSignal.
  useMenuChangeSignal(
    { menuId: contentMenuId ?? menu?.id ?? null, branchId: branch?.id ?? null, templateId: template?.id ?? null },
    queueMenuReload,
    [
      ['menu', 'slug', slug],
      ['menu-categories', contentMenuId],
      ['menu-collections', contentMenuId],
      ['menu-items', contentMenuId],
      ['menu-promotions', menu?.id],
      ['templates', template?.id],
      ['branch-menu-overrides', branch?.id],
    ],
  );

  const qrScanEnabled = searchParams?.get('qrcode') === 'true';
  const zoneCode = searchParams?.get('zone_code') || null;
  // Q2: `table_code` is the single canonical table param (the only one the QR
  // generator emits). The `table` / `tableCode` aliases were attack/support
  // surface , a URL meaning had two undocumented spellings , so they are gone.
  const tablePublicCode = searchParams?.get('table_code') || null;
  const tableSeatLabel =
    searchParams?.get('seat') ||
    searchParams?.get('seatLabel') ||
    searchParams?.get('chair') ||
    null;

  useEffect(() => {
    if (!menu?.id || !tablePublicCode) {
      if (menu?.id) {
        const cachedLatest = readLatestStoredPublicTableSession(menu.id);
        setTableSession(cachedLatest);
      } else {
        setTableSession(null);
      }
      setTableSessionError(null);
      setIsResolvingTableSession(false);
      return;
    }

    const cached = readStoredPublicTableSession(menu.id, tablePublicCode);
    if (cached) {
      setTableSession(cached);
    }

    let active = true;
    const resolveTableSession = async () => {
      setIsResolvingTableSession(true);
      setTableSessionError(null);

      try {
        const response = await fetch(resolveApiRequestUrl('/api/public/table-sessions/resolve'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            publicCode: tablePublicCode,
            seatLabel: tableSeatLabel || undefined,
          }),
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const errorCode =
            payload && typeof payload === 'object' && 'code' in payload
              ? String((payload as { code?: unknown }).code || '').toUpperCase()
              : '';
          if (response.status === 401 || response.status === 403 || errorCode === 'SESSION_EXPIRED') {
            throw new Error('Session expired , scan the QR code again.');
          }
          const message =
            payload && typeof payload === 'object' && 'error' in payload
              ? String((payload as { error?: unknown }).error || 'Could not resolve this table session.')
              : 'Could not resolve this table session.';
          throw new Error(message);
        }

        const resolved = payload as PublicResolvedTableSession;
        if (menu.branch_id && resolved.table.branch_id !== menu.branch_id) {
          throw new Error('This QR code belongs to a different branch than the menu you opened.');
        }

        if (!active) {
          return;
        }

        writeStoredPublicTableSession(menu.id, tablePublicCode, resolved);
        setTableSession(resolved);
        cleanResolvedScanUrl();
      } catch (error) {
        if (!active) {
          return;
        }

        clearStoredPublicTableSession(menu.id, tablePublicCode);
        setTableSession(null);
        setTableSessionError(
          error instanceof Error ? error.message : 'Could not resolve this table session.',
        );
      } finally {
        if (active) {
          setIsResolvingTableSession(false);
        }
      }
    };

    void resolveTableSession();

    return () => {
      active = false;
    };
  }, [menu?.id, menu?.branch_id, tablePublicCode, tableSeatLabel]);

  // Doc 51 §3.3 , the /q/{token} cookie hand-off. When there is no ?table_code=
  // param (a /q redirect leaves none) and nothing usable is stored, ask the
  // server once for the session the HttpOnly cookie carries, then treat it
  // exactly like a resolve success so ordering sees the same state. A plain
  // menu visit (no ?qrcode=true) skips the network call when a stored session
  // already covers this menu.
  //
  // The ref is reset when the scan context changes (a fresh ?qrcode=true scan,
  // a new table_code, a different menu) so a guest who scans a second QR after
  // navigating away gets the new cookie's session instead of the first one.
  useEffect(() => {
    const scanSignature = `${menu?.id ?? ''}|${menu?.branch_id ?? ''}|${tablePublicCode ?? ''}|${zoneCode ?? ''}|${qrScanEnabled}`;
    if (lastScanSignatureRef.current !== scanSignature) {
      lastScanSignatureRef.current = scanSignature;
      cookieSessionCheckedRef.current = false;
    }
    if (cookieSessionCheckedRef.current) return;
    if (!menu?.id || tablePublicCode || zoneCode) {
      return;
    }
    if (
      !qrScanEnabled &&
      isStoredPublicTableSessionUsable(readLatestStoredPublicTableSession(menu.id))
    ) {
      return;
    }

    cookieSessionCheckedRef.current = true;
    let active = true;
    const loadCookieSession = async () => {
      try {
        const response = await fetch(resolveApiRequestUrl('/api/public/table-sessions/current'), {
          cache: 'no-store',
        });
        const payload = await response.json().catch(() => null);
        if (!active) return;
        if (!response.ok || !payload?.ok || !payload?.sessionToken) return;
        const resolved = payload as PublicResolvedTableSession;
        if (menu.branch_id && resolved.table?.branch_id !== menu.branch_id) return;
        // Store under the session's own identity, never a literal sentinel: the
        // `latest` pointer must track a real session so a later browse-mode
        // visit resumes the right table instead of a shadowed `__cookie__` slot.
        // A resolved session always carries `session_code` or `table.id`, so the
        // sentinel fallback below is unreachable in practice — it exists only to
        // keep the storage key a string if the server payload ever changes shape.
        const sessionKey = resolved.session?.session_code || resolved.table?.id || '__cookie__';
        writeStoredPublicTableSession(menu.id, sessionKey, resolved);
        setTableSession(resolved);
      } catch {
        // Browse mode is the fallback when the cookie can't be read.
      }
    };
    void loadCookieSession();
    return () => {
      active = false;
    };
  }, [menu?.id, menu?.branch_id, tablePublicCode, zoneCode, qrScanEnabled]);

  useEffect(() => {
    if (!tableSessionError) {
      return;
    }

    toast.error(tableSessionError);
  }, [tableSessionError]);

  // Doc 64 §8: consume the /q redirect's ?qr_error=<CODE> once. The code maps to
  // a friendly message that feeds the same tableSessionError state (and its toast)
  // as the legacy resolve flow, then the param is stripped so a refresh does not
  // re-show it. Only qr_error is removed — qrcode=true and table_code stay.
  useEffect(() => {
    const qrErrorCode = searchParams?.get('qr_error');
    if (!qrErrorCode) {
      return;
    }

    setTableSessionError(
      QR_ERROR_MESSAGES[qrErrorCode] ?? QR_ERROR_MESSAGES.UNKNOWN,
    );

    const url = new URL(window.location.href);
    if (url.searchParams.has('qr_error')) {
      url.searchParams.delete('qr_error');
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!menu?.id || !tableSession?.branch.id || !tableSession?.table.id) {
      return;
    }

    let active = true;
    const resolveScopedTemplate = async () => {
      try {
        const params = new URLSearchParams({
          menuId: menu.id,
          branchId: tableSession.branch.id,
          tableId: tableSession.table.id,
        });
        if (tableSession.table.zone_id) {
          params.set('zoneId', tableSession.table.zone_id);
        }
        const response = await timedFetch(resolveApiRequestUrl(`/api/seasonal-rules/resolve?${params.toString()}`), {
          cache: 'no-store',
        });
        const payload = await response.json().catch(() => null);
        if (!active) return;
        if (response.ok && payload?.template) {
          setTemplate(payload.template as TemplateRecord);
        } else if (!response.ok) {
          console.error('[public-menu:scoped-template]', payload?.error || 'Scoped template resolution failed.');
        }
      } catch (error) {
        console.error('[public-menu:scoped-template]', error);
      }
    };

    void resolveScopedTemplate();
    return () => {
      active = false;
    };
  }, [menu?.id, tableSession?.branch.id, tableSession?.table.id, tableSession?.table.zone_id, timedFetch]);

  useEffect(() => {
    setFeedbackOpen(false);
    setFeedbackRating(0);
    setFeedbackNote('');
    setFeedbackSubmitted(false);
  }, [tableSession?.session.id]);

  useEffect(() => {
    if (!menu?.id || !tableSession?.session.id) {
      return;
    }

    const trackingKey = `table-session:${menu.id}:${tableSession.session.id}`;
    if (trackedEventsRef.current.has(trackingKey)) {
      return;
    }

    trackedEventsRef.current.add(trackingKey);
    const sessionType = isCounterPickupSession ? 'counter' : 'table';
    void trackPublicMenuEvent({
      menuId: menu.id,
      eventType: 'qr_session_resolved',
      actionTarget: tableSession.table.order_mode,
      metadata: {
        business_id: tableSession.branch.business_id,
        branch_id: tableSession.branch.id,
        table_id: tableSession.table.id,
        zone_id: tableSession.table.zone_id || null,
        table_label: tableSession.table.label,
        session_id: tableSession.session.id,
        session_type: sessionType,
        qr_kind: sessionType,
        session_code: tableSession.session.session_code,
        seat_label: tableSession.seatLabel || null,
      },
    });
    void trackPublicMenuEvent({
      menuId: menu.id,
      eventType: isCounterPickupSession ? 'counter_session_view' : 'table_session_view',
      actionTarget: tableSession.table.id,
      metadata: {
        business_id: tableSession.branch.business_id,
        branch_id: tableSession.branch.id,
        table_id: isCounterPickupSession ? null : tableSession.table.id,
        zone_id: isCounterPickupSession ? null : tableSession.table.zone_id || null,
        session_id: tableSession.session.id,
        session_type: sessionType,
        qr_kind: sessionType,
      },
    });
  }, [menu?.id, tableSession, isCounterPickupSession]);

  useEffect(() => {
    if (!menu?.id) {
      return;
    }

    const result = beginPublicMenuAnalyticsSession(menu.id);
    if (!result) {
      return;
    }
    const session = result.session;
    analyticsSessionRef.current = session;

    const sessionStartKey = `session_start:${menu.id}:${session.sessionId}`;
    const sessionStartIso = new Date(session.startedAt).toISOString();

    if (result.isNewVisit && !trackedEventsRef.current.has(sessionStartKey)) {
      trackedEventsRef.current.add(sessionStartKey);
      void trackPublicMenuEvent({
        menuId: menu.id,
        eventType: 'session_start',
        sessionIdOverride: session.sessionId,
        metadata: {
          session_started_at: sessionStartIso,
        },
      });
    }

    const viewKey = `view:${menu.id}:${session.sessionId}`;
    if (!trackedEventsRef.current.has(viewKey)) {
      trackedEventsRef.current.add(viewKey);
      void trackPublicMenuEvent({
        menuId: menu.id,
        eventType: 'view',
        sessionIdOverride: session.sessionId,
      });
    }

    const endSession = (reason: 'pagehide' | 'unmount') => {
      const sessionEndKey = `session_end:${menu.id}:${session.sessionId}`;
      if (trackedEventsRef.current.has(sessionEndKey)) {
        return;
      }

      trackedEventsRef.current.add(sessionEndKey);
      const endedAt = new Date().toISOString();
      const durationSeconds = Math.max(1, Math.round((Date.now() - session.startedAt) / 1000));
      void trackPublicMenuEvent({
        menuId: menu.id,
        eventType: 'session_end',
        sessionIdOverride: session.sessionId,
        transport: 'beacon',
        metadata: {
          session_started_at: sessionStartIso,
          session_ended_at: endedAt,
          duration_seconds: durationSeconds,
          end_reason: reason,
        },
      });
    };

    const handlePageHide = () => {
      endSession('pagehide');
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      endSession('unmount');
    };
  }, [menu?.id]);

  useEffect(() => {
    if (menu?.id) {
      const sessionKey = `scan:${menu.id}:${qrScanEnabled ? 'qr' : 'standard'}`;
      if (qrScanEnabled && !trackedEventsRef.current.has(sessionKey)) {
        trackedEventsRef.current.add(sessionKey);
        void trackPublicMenuEvent({ menuId: menu.id, eventType: 'scan' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu?.id, qrScanEnabled]);

  const businessInfo = business || presentedMenuData?.business || {};
  const businessSlug = businessInfo?.slug || null;

  const isEmbeddedView = useMemo(() => {
    if (!searchParams) return false;

    const queryKeys = ['embed', 'noHeader', 'no_header', 'hideHeader', 'hide_header'];
    const trueValues = new Set(['1', 'true', 'yes', 'on']);
    const falseValues = new Set(['0', 'false', 'no', 'off']);

    for (const key of queryKeys) {
      const rawValue = searchParams.get(key);
      if (rawValue === null) continue;

      const normalized = rawValue.trim().toLowerCase();
      if (trueValues.has(normalized)) return true;
      if (falseValues.has(normalized)) return false;
      return false;
    }

    return false;
  }, [searchParams]);
  /**
   * A legacy-scroll layout, a storefront layout and a paged-flip pager all once
   * painted the same branch three different ways. That divergence is gone: the
   * customer surface renders every engine through Menuraq's own menu body. All
   * that survives here is the merchant's layout preference for that body.
   */

  /** `rows` unless the template or branch says otherwise — the only layout that stays
   *  legible on a long menu. */
  /**
   * Branch setting wins over the template's, because the branch is the thing a
   * merchant actually administers — asking them to author a template just to
   * choose between three layouts is the wrong shape.
   */
  /**
   * The layout the merchant actually configured, or null when they did not.
   *
   * V1 and V2 disagree about the fallback on purpose. V1's platform list is a
   * dense single column, because that is the layout the original menu was
   * designed around. V2 is the Chow app on the web, and Chow's default is the
   * two-up card grid on a phone (3 tablet / 4 desktop). Keeping the old `rows`
   * fallback here made `?experience=v2` open as a list while the app opened as
   * a grid, which is the one difference a guest notices immediately.
   */
  const configuredStorefrontLayout = (initialMenu.storefrontLayout
    ?? (resolvedTemplateSchema as { storefrontLayout?: string } | null)?.storefrontLayout
    ?? null) as 'rows' | 'cards' | 'showcase' | null;
  const storefrontLayout = (configuredStorefrontLayout ?? 'rows') as
    | 'rows'
    | 'cards'
    | 'showcase';
  const v2StorefrontLayout = (configuredStorefrontLayout ?? 'cards') as
    | 'rows'
    | 'cards'
    | 'showcase';

  // The server resolves branch/business assignment precedence once. Use that
  // same palette for inline surfaces and portals, not just inherited CSS.
  const branchTheme = useContext(BranchThemeContext);
  const templateSurfacePalette = useMemo(
    () => branchTheme?.[publicThemeMode]
      ?? resolveTemplateSurfacePalette(resolvedTemplateSchema, publicThemeMode),
    [branchTheme, resolvedTemplateSchema, publicThemeMode],
  );
  const guestPaletteStyle = useMemo(
    () => guestV2PaletteVars(templateSurfacePalette, publicThemeMode),
    [templateSurfacePalette, publicThemeMode],
  );
  const templateSurfaceStyle = useMemo(
    () => ({
      ...resolveTemplateSurfaceStyle(resolvedTemplateSchema, publicThemeMode, templateSurfacePalette),
      ...guestPaletteStyle,
      background: branchTheme?.backgroundPaint?.[publicThemeMode] ?? templateSurfacePalette.background,
      color: templateSurfacePalette.foreground,
    }),
    [resolvedTemplateSchema, publicThemeMode, guestPaletteStyle, templateSurfacePalette, branchTheme],
  );
  const loadingPalette = templateSurfacePalette;
  // The outer shell is ONE canonical design for every template (flipbook,
  // legacy shopfront, platform default alike) — header, ribbon, nav, rail and
  // footer all visible, sheet dialogs, one promotion fallback slot. A template
  // owns its BODY (palette, fonts, cards, storefront layout) and nothing else,
  // so a flipbook menu and a shopfront menu present through the same chrome.
  const shellConfig = useMemo(
    () => CUSTOMER_UNIFIED_SHELL_CONFIG,
    [],
  );
  const promotionPresentation = useMemo(
    () => resolveTemplatePromotionPresentationConfig(resolvedTemplateSchema),
    [resolvedTemplateSchema],
  );
  const canPublicClaimPromotions = useMemo(
    () =>
      Boolean(branchPosAddonActive && branchPosFeatures.guestOrdering && tableSession && !isCounterPickupSession) &&
      (promotions || []).some((promotion) => promotion.application_mode === 'public_claim' && isPromotionActive(promotion)),
    [branchPosAddonActive, branchPosFeatures.guestOrdering, promotions, tableSession, isCounterPickupSession],
  );
  const rendererData = useMemo(() => {
    // Search overrides category/collection narrowing: when a query is active the
    // guest wants matches across the whole menu, so use the full (search-filtered)
    // menuData rather than the per-category presented slice.
    //
    // V2 is the exception, because Chow mobile combines them: the query narrows
    // every tab and the active tab narrows the results. `presentedMenuData` is
    // already [collection → category] over the *search-filtered* menu, so using
    // it here gives the app's behaviour without a second filtering path.
    //
    // Otherwise the narrowed slice always wins. It used to be bypassed for
    // paged-flip templates, which is why tapping a category on a flipbook menu
    // changed the chips but not the pages.
    const baseData = experienceV2 || !searchQuery.trim() ? presentedMenuData : effectiveMenuData;
    if (!baseData) return undefined;

    const promotionsVisible = (promotions || []).length > 0;
    const scopedData = resolveGuestMenuRendererData(baseData, {
      showPromotions: promotionsVisible,
      enableClaimAction: canPublicClaimPromotions,
    });
    if (!scopedData) return undefined;

    return {
      ...scopedData,
      actions: {
        ...(scopedData.actions || {}),
        ...(canPublicClaimPromotions ? { claimPromotion: handleClaimPromotion } : {}),
      },
    };
  }, [effectiveMenuData, presentedMenuData, searchQuery, experienceV2, promotions, canPublicClaimPromotions, handleClaimPromotion]);
  const templateOwnsPromotions = useMemo(
    () => templateOwnsPromotionPlacement(resolvedTemplateSchema),
    [resolvedTemplateSchema],
  );

  const publicActions = useMemo<PublicMenuAction[]>(
    () =>
      buildPublicGuestActions(
        resolveBranchGuestActions({
          branch,
          business: businessInfo,
        }),
      ),
    [branch, businessInfo],
  );

  const deliveryAction = useMemo(
    () => publicActions.find((action) => action.key === 'delivery') || publicActions.find((action) => action.key === 'order') || null,
    [publicActions],
  );

  // Feedback → public-review funnel (doc 36): if the branch has configured a public
  // review link (guest-action metadata), a happy rating is routed there after the
  // private feedback is saved; an unhappy one is kept private. Reputation protection ,
  // the merchant hears the bad ones first, and only the good ones get pushed public.
  const reviewUrl = useMemo(
    () => resolveBranchGuestActions({ branch, business: businessInfo }).reviewUrl ?? null,
    [branch, businessInfo],
  );

  // Business-type customer CTA (docs/expansion-roadmap-2026/33 + 34): a caterer's
  // public page leads with "Book an event" pointing at the branded booking flow
  // (/book/[menuId]). Presentation only -- appended to the existing per-branch guest
  // actions, never replacing them; standard/bar are unaffected (their customerEntry
  // isn't 'booking'). Falls back safely when business_type is absent (-> 'standard').
  const experienceQuery = experienceV2 ? '?experience=v2' : '';
  const bookingHref = menu?.id ? `/book/${menu.id}${experienceQuery}` : null;

  // "Reserve a table" (doc 36) , surfaced for dine-in types (standard/bar) only when
  // the branch actually has reservations enabled, so it's never a dead-end link.
  const reserveHref = menu?.slug ? `/reserve/${menu.slug}${experienceQuery}` : null;

  /**
   * Resolved on the SERVER (`loadPublicMenuPageData`), not fetched after mount.
   *
   * This used to be a `useState(false)` that a client fetch flipped true a
   * second or two after paint, which made the bottom rail grow a fourth tab
   * under the guest — the reflow `resolvePlaceTabs` explicitly forbids. It also
   * cost a round trip on café Wi-Fi to learn something the server already knew.
   */
  const reserveEnabled = useMemo(() => {
    if (!Boolean(initialMenu.reservationsEnabled)) return false;
    const entry = getBusinessType(branch?.business_type).presentation.customerEntry;
    return entry === 'menu' || entry === 'tab_menu';
  }, [initialMenu.reservationsEnabled, branch?.business_type]);

  /**
   * Non-ordering intents offered in the arrival sheet (MNRQ-UX-02, doc 71):
   * "Reserve a table" and "Plan an event" are things a guest might have come
   * to do, phrased as goals rather than product modes. Each is a destination,
   * not a fulfilment mode — confirming navigates to the reserve/book surface.
   *
   * The conditions are the SAME ones `shellActions` uses, so the sheet never
   * offers a dead-end: reservations only when the branch has them enabled,
   * event booking only when the business type's whole entry is booking.
   */
  const arrivalDestinations = useMemo(() => {
    const list: {
      key: string;
      label: string;
      description: string;
      href: string;
    }[] = [];
    if (reserveEnabled && reserveHref) {
      list.push({
        key: 'reserve',
        label: 'Reserve a table',
        description: 'Hold a table for your visit',
        href: reserveHref,
      });
    }
    const entry = getBusinessType(branch?.business_type).presentation.customerEntry;
    if (entry === 'booking' && bookingHref) {
      list.push({
        key: 'book',
        label: 'Plan an event',
        description: 'Book a date for your event',
        href: bookingHref,
      });
    }
    return list;
  }, [reserveEnabled, reserveHref, bookingHref, branch?.business_type]);

  // "Buy a gift card" (doc 36) , shown only when the business's plan includes gift
  // cards (server-resolved flag; the public purchase endpoint enforces the same).
  const giftCardsEnabled = Boolean(initialMenu.giftCardsEnabled);
  const giftCardHref = menu?.slug ? `/gift-cards/buy/${menu.slug}` : null;

  const shellActions = useMemo<PublicMenuAction[]>(() => {
    const entry = getBusinessType(branch?.business_type).presentation.customerEntry;
    let list: PublicMenuAction[];
    if (entry === 'booking' && bookingHref) {
      const bookAction: PublicMenuAction = {
        key: 'book',
        label: word(branch?.business_type, 'placeOrderCta', branch?.specific_type),
        href: bookingHref,
        external: false,
      };
      list = [bookAction, ...publicActions.filter((action) => action.key !== 'book')];
    } else if (reserveEnabled && reserveHref) {
      const reserveAction: PublicMenuAction = {
        key: 'reserve',
        label: 'Reserve a table',
        href: reserveHref,
        external: false,
      };
      list = [...publicActions.filter((action) => action.key !== 'reserve'), reserveAction];
    } else {
      list = publicActions;
    }
    if (giftCardsEnabled && giftCardHref) {
      list = [
        ...list.filter((action) => action.key !== 'gift_card'),
        { key: 'gift_card', label: 'Gift cards', href: giftCardHref, external: false },
      ];
    }
    return list;
  }, [branch?.business_type, bookingHref, reserveHref, reserveEnabled, publicActions, giftCardsEnabled, giftCardHref]);

  // The type's headline conversion action, pinned as an always-visible CTA (QA
  // 2026-07-20 P2-6): a caterer's browse-only menu previously hid "Book an
  // event" inside the collapsed actions panel , a corporate planner had no
  // visible path from the menu into the booking/quote pipeline.
  const primaryShellAction = useMemo<PublicMenuAction | null>(() => {
    if (isEmbeddedView || tableSession) return null;
    const entry = getBusinessType(branch?.business_type).presentation.customerEntry;
    if (entry !== 'booking' || !bookingHref) return null;
    return {
      key: 'book',
      label: word(branch?.business_type, 'placeOrderCta', branch?.specific_type),
      href: bookingHref,
      external: false,
    };
  }, [isEmbeddedView, tableSession, branch?.business_type, bookingHref]);

  const customerModes = useMemo<PublicMenuMode[]>(() => {
    const modes: PublicMenuMode[] = [
      {
        key: 'menu',
        label: 'Browse',
        description: 'View menu',
      },
    ];

    // Dine-in , requires a table QR session
    if (tableSession && !isCounterPickupSession && branchOrderingEnabled) {
      modes.push({
        key: 'dine_in',
        label: 'Eat here',
        description: tableSession.table.label,
      });
    }

    // Pickup , session-free, gated by operating mode + setting.
    // `pickup` operates as a superset of `counter_pickup` on the public menu:
    // if the explicit pickup mode is not stored (it was added to the code-level
    // OPERATING_MODES list before the DB CHECK constraint was updated) fall back
    // to the always-default counter_pickup so branches aren't blocked from
    // offering session-free walk-in ordering.
    // Doc 70 F10. This used to ALSO require
    // `branchOrderingSettings.counterPickupEnabled`, a legacy boolean column
    // that vetoed pickup on 37 of the 39 branches whose operating-mode registry
    // had it switched on. 108 of 112 branches sit at its creation default of
    // false, so the veto was almost never a merchant decision — it was the
    // reason most branches showed a single mode, which in turn meant no mode
    // picker, no mode switcher and no arrival question anywhere in the product.
    // The registry is the authority, and the server enforces the same rule.
    if (activeOperatingModes.pickup || activeOperatingModes.counter_pickup) {
      modes.push({
        key: 'pickup',
        label: 'Pick up later',
        description: 'Order ahead, collect in person',
      });
    }

    // Delivery , session-free, in-app ordering gated by operating mode.
    // When external delivery is configured (deliveryAction?.href), it's shown
    // in the guest actions panel instead of the mode selector, so the user
    // isn't surprised by an out-of-app redirect.
    if (activeOperatingModes.delivery_managed && !deliveryAction?.href) {
      modes.push({
        key: 'delivery',
        label: 'Deliver to me',
        description: 'Brought to the address you give — sign-in required',
      });
    }

    // Scheduled pre-order , session-free, gated by operating mode
    if (activeOperatingModes.scheduled_pre_order) {
      modes.push({
        key: 'scheduled',
        label: 'Pre-order',
        description: 'Book a pickup slot',
      });
    }

    return modes;
  }, [
    activeOperatingModes,
    branchOrderingSettings,
    branchOrderingEnabled,
    isCounterPickupSession,
    tableSession,
  ]);

  // Show the arrival sheet only when there is genuinely more than one mode to
  // choose from (nothing to pick otherwise) and it hasn't been resolved yet.
  //
  // Must sit BELOW the `customerModes` memo it reads and ABOVE the browse-click
  // effect that closes over it. Hoisting it to the state declarations at the top
  // of the component puts it in `customerModes`' temporal dead zone, which
  // throws on every render and takes the whole public menu down with it.
  /**
   * The real ways this branch can get food to a guest right now. `menu`
   * ("Browse") is deliberately excluded: browsing is the absence of a
   * fulfilment choice, not one of the options, and offering it as a peer of
   * Pickup and Delivery is what put most guests in a mode where nothing was
   * orderable. Present it as a choice and roughly half of them will pick it.
   */
  const orderModes = useMemo(
    () => customerModes.filter((mode) => mode.key !== 'menu'),
    [customerModes],
  );

  /**
   * What the guest is actually OFFERED — doc 70 §3.
   *
   * `orderModes` above deliberately drops Browse, and that was right for
   * deciding which mode to ENTER: presenting "Browse" as a peer of Pickup meant
   * roughly half of guests picked the one option that cannot produce an order.
   *
   * But it also made Browse unchoosable. A guest who genuinely only wants to
   * read the menu — one of the two reasons anyone opens a menu URL — had no way
   * to say so, and the page instead put them in a fulfilment mode they never
   * asked for. So Browse is offered here, and offered LAST, so the guest who
   * came to order never has to read past it to find their option.
   *
   * The distinction that makes this safe: Browse is never the DEFAULT and is
   * never what a dismissal lands on. It is only ever reachable by a guest
   * deliberately choosing it.
   */
  const selectableModes = useMemo<PublicMenuMode[]>(() => {
    if (orderModes.length === 0) return [];
    const browse = customerModes.find((mode) => mode.key === 'menu');
    return browse
      ? [...orderModes, { ...browse, label: 'Just looking', description: 'See the menu without ordering' }]
      : [...orderModes];
  }, [orderModes, customerModes]);

  /** Ask only when there is genuinely more than one answer. One way of getting
   *  food is not a question, it is a fact, and the page just applies it. */
  const fulfilmentChoiceExists = Boolean(menu?.id) && orderModes.length > 1;

  /**
   * The switch V2 shows INSIDE checkout — Chow asks "how do I get this food?"
   * at the moment the guest commits, not in a sheet before they have seen the
   * menu. Only the self-service modes are offered: a scanned table already
   * answered the question, and a scheduled pre-order is tied to its slot.
   */
  const v2CheckoutFulfilment = useMemo(() => {
    if (!experienceV2) return [];
    const options: { value: 'pickup' | 'delivery'; label: string }[] = [];
    for (const mode of orderModes) {
      if (mode.key === 'pickup') options.push({ value: 'pickup', label: mode.label || 'Pickup' });
      if (mode.key === 'delivery') options.push({ value: 'delivery', label: mode.label || 'Delivery' });
    }
    return options;
  }, [experienceV2, orderModes]);

  const handleCustomerModeChange = useCallback(
    (mode: PublicMenuMode) => {
      // A mode change abandons any "schedule for next opening" prefill; the
      // customer picked a different path, so don't carry the time over.
      setNextOpenScheduledTime(null);
      setNextOpenBannerError(null);
      setCustomerMode(mode.key);
      rememberCustomerMode(menu?.slug, mode.key);

      // External delivery link (legacy) , navigate away
      if (mode.key === 'delivery' && mode.href) {
        const isExternal = mode.external !== false && !mode.href.startsWith('/');
        if (isExternal) {
          window.open(mode.href, '_blank', 'noopener,noreferrer');
          return;
        }
        window.location.href = mode.href;
        return;
      }

      // In-app modes (pickup, dine_in, scheduled) , handled by the ordering experience components
    },
    [menu?.slug],
  );

  /**
   * The ordering mode a guest can actually use right now, in preference order
   * (dine-in beats session-free modes so a scanned table keeps its context).
   * When a guest taps a menu item in browse mode and one of these exists, the
   * tap routes into that ordering surface instead of the read-only detail
   * sheet , otherwise items with varieties/options/add-ons can never be
   * customised from browse mode, which dead-ends anyone who wants to order.
   */
  const bestAvailableOrderMode = useMemo<PublicMenuMode['key'] | null>(() => {
    for (const key of ['dine_in', 'pickup', 'delivery', 'scheduled'] as const) {
      if (customerModes.some((m) => m.key === key)) return key;
    }
    return null;
  }, [customerModes]);

  // Chow owns item taps for both menu bodies and every ordering mode. The
  // ordering island disables its competing delegated handler, so template
  // renderers and platform cards both open the same single details sheet.
  // V1 keeps this page handler only while it is in browse mode.
  useEffect(() => {
    if (!experienceV2 && !isBrowseMode) return;
    const openFromTarget = (target: HTMLElement | null) => {
      const el = target ? target.closest<HTMLElement>('[data-item-id]') : null;
      if (!el) return;
      const itemId = el.dataset.itemId?.trim();
      if (!itemId) return;
      const item = browseItemsById.get(itemId);
      if (!item) return;
      // TemplateRenderer items do not have React click props, so they arrive
      // through this delegate instead of PlaceMenuBody's `onOpenItem` callback.
      // Keep the same Chow path here: the ordering sheet starts with details
      // and item instructions, then opens choices only when requested.
      if (experienceV2 && bestAvailableOrderMode) {
        if (customerMode === 'menu') {
          const mode = customerModes.find((candidate) => candidate.key === bestAvailableOrderMode);
          if (mode) handleCustomerModeChange(mode);
        }
        setBrowseOrderItem(item);
        return;
      }
      setBrowseDetailItem(item);
      setBrowseDetailOpen(true);
    };
    // Resolve the menu item behind a click/keydown target. Returns the item
    // only when the target (or an ancestor) is a known [data-item-id] element ,
    // otherwise returns null. This guard is essential: shell chrome, pager,
    // panels, and the chat widget must never have their events swallowed.
    const resolveBrowseItem = (target: HTMLElement | null) => {
      if (!target) return null;
      const el = target.closest<HTMLElement>('[data-item-id]');
      if (!el) return null;
      const itemId = el.dataset.itemId?.trim();
      if (!itemId) return null;
      return browseItemsById.get(itemId) ?? null;
    };
    const handleClick = (event: MouseEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!resolveBrowseItem(target)) return;
      event.preventDefault();
      event.stopPropagation();
      openFromTarget(target);
    };
    // Keyboard access for menu items. Native buttons/links activate on
    // Enter (keydown + click), so double-opening is possible and is harmless ,
    // the detail sheet is idempotent to open. Non-focusable template elements
    // still need a role/tabIndex (template renderers, out of scope) to receive this.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!resolveBrowseItem(target)) return;
      event.preventDefault();
      event.stopPropagation();
      openFromTarget(target);
    };
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    isBrowseMode,
    experienceV2,
    browseItemsById,
    bestAvailableOrderMode,
    customerMode,
    customerModes,
    handleCustomerModeChange,
  ]);

  /**
   * Which mode the page STARTS in.
   *
   * THE CHANGE THAT MATTERS MOST ON THIS PAGE. This used to fall back to
   * `'menu'` — "Browse" — whenever no session and no remembered preference
   * existed, which was the overwhelming majority of first visits. Browse does
   * not mount the ordering island, so nothing on the menu could be added to a
   * cart; the guest's only signal that anything was wrong was a dead-end line
   * in the item sheet reading "Scan the QR code at your table to order this",
   * on branches that were perfectly happy to do pickup.
   *
   * A menu a guest cannot order from is the single biggest thing testers ran
   * into, and it was the DEFAULT. So the page now enters the best real ordering
   * mode it has and the menu is live from the first tap. Choosing between two
   * real modes is a small correction the guest makes in the action bar, with
   * food already in the cart — not a gate in front of the food.
   *
   * `'menu'` survives as the honest answer for a branch that genuinely takes no
   * orders. There it is not a mode the guest picked; it is the absence of one,
   * and nothing in the UI offers it as a choice.
   */
  const initialCustomerMode = useMemo<PublicMenuMode['key']>(() => {
    if (tableSession && branchOrderingEnabled) {
      return isCounterPickupSession ? 'pickup' : 'dine_in';
    }
    const remembered = readRememberedCustomerMode(menu?.slug);
    if (remembered && orderModes.some((m) => m.key === remembered)) return remembered;
    return bestAvailableOrderMode ?? 'menu';
  }, [
    branchOrderingEnabled,
    orderModes,
    bestAvailableOrderMode,
    isCounterPickupSession,
    menu?.slug,
    tableSession,
  ]);

  useEffect(() => {
    // Table session takes priority , auto-select dine-in or legacy counter pickup.
    // Once per session, not on every run: this effect re-runs whenever the mode list
    // is rebuilt (menu reloads, realtime invalidations), and forcing the mode each
    // time snapped a guest who had chosen delivery straight back to pickup. On a
    // phone that had scanned a counter QR that made delivery impossible to pick.
    if (tableSession && branchOrderingEnabled) {
      const sessionKey = tableSession.session?.id || tableSession.session?.session_code || tableSession.table?.id || 'session';
      if (sessionModeAppliedRef.current !== sessionKey) {
        sessionModeAppliedRef.current = sessionKey;
        modeRestoredRef.current = true;
        setCustomerMode(isCounterPickupSession ? 'pickup' : 'dine_in');
        return;
      }
      setCustomerMode((current) =>
        customerModes.some((m) => m.key === current) ? current : isCounterPickupSession ? 'pickup' : 'dine_in',
      );
      return;
    }
    sessionModeAppliedRef.current = null;

    // First resolution once the available modes are known. Runs after the
    // table-session check because a scanned table always outranks anything
    // remembered or inferred.
    if (!modeRestoredRef.current && customerModes.length > 0) {
      modeRestoredRef.current = true;
      // A reorder (doc 54 / C2) has a definite intent: land the guest directly
      // in a session-free ordering mode so the prefilled cart is one tap from
      // checkout. Outranks anything else.
      if (initialCart && initialCart.length > 0) {
        const preferred = ['pickup', 'delivery', 'scheduled'] as const;
        const chosen = preferred.find((key) => customerModes.some((m) => m.key === key));
        if (chosen) {
          setCustomerMode(chosen);
          return;
        }
      }
      setCustomerMode(initialCustomerMode);
      // A remembered mode IS a previous answer to "how would you like this?" —
      // `readRememberedCustomerMode` only ever stores a real fulfilment mode,
      // never browse. Without this a guest who answered the question, then
      // reloaded (or came back with items still in the cart), was asked it
      // again with their own earlier answer already applied underneath. Set in
      // the effect rather than the state initialiser so the server and client
      // render the same thing.
      if (readRememberedCustomerMode(menu?.slug)) setFulfilmentAnswered(true);
      return;
    }

    // Afterwards, keep whatever the guest is in as long as it is still offered,
    // and fall back to the best remaining ordering mode rather than dropping
    // them into an unorderable menu when one disappears.
    setCustomerMode((current) => {
      if (current !== 'menu' && customerModes.some((m) => m.key === current)) return current;
      return bestAvailableOrderMode ?? 'menu';
    });
  }, [
    branchOrderingEnabled,
    customerModes,
    bestAvailableOrderMode,
    initialCustomerMode,
    isCounterPickupSession,
    tableSession,
    menu?.slug,
    initialCart,
  ]);

  // One-time notice when the cart was prefilled from a reorder/usual link.
  useEffect(() => {
    if (reorderNoticeShownRef.current) return;
    if (!initialCart?.length && !reorderNotice?.cartEmpty) return;
    reorderNoticeShownRef.current = true;
    if (reorderNotice?.cartEmpty) {
      toast.info(
        reorderNotice?.emptyLabel ??
          'We couldn\u2019t rebuild this order \u2014 the menu has changed. Add your items fresh below.',
      );
      return;
    }
    const prefix =
      reorderNotice?.label ??
      (reorderNotice?.orderNumber
        ? `Order #${reorderNotice.orderNumber} is in your cart.`
        : 'Your last order is in your cart.');
    const suffix = reorderNotice?.skippedCount
      ? ` ${reorderNotice.skippedCount} item${reorderNotice.skippedCount > 1 ? 's' : ''} couldn\u2019t be repeated.`
      : '';
    toast.success(`${prefix} Review it, then place your order.${suffix}`);
  }, [initialCart, reorderNotice]);

  const branchOpenNow = useMemo<boolean | null>(() => {
    const hours = readBranchOperatingHours(branch?.operating_hours);
    const hasConfiguredHours = Object.values(hours).some((day) => !day.closed);
    if (!hasConfiguredHours) return null;
    return isBranchOpenNow(hours, branch?.timezone || 'UTC');
  }, [branch?.operating_hours, branch?.timezone]);

  const publicBranchDetails = useMemo<PublicMenuBranchDetails>(
    () => ({
      name: branch?.name || null,
      address: branch?.address || businessInfo?.address || businessInfo?.location || null,
      phone: branch?.phone || businessInfo?.phone || null,
      email: branch?.email || businessInfo?.email || null,
      timezone: branch?.timezone || null,
      hoursSummary: formatBranchHoursSummary(branch?.operating_hours, branch?.timezone, branchOpenNow),
      locationLabel: branch?.current_location_label || businessInfo?.location || null,
    }),
    [
      branch?.address,
      branch?.current_location_label,
      branch?.email,
      branch?.name,
      branch?.operating_hours,
      branch?.phone,
      branch?.timezone,
      branchOpenNow,
      businessInfo?.address,
      businessInfo?.email,
      businessInfo?.location,
      businessInfo?.phone,
    ],
  );

  const handlePublicActionClick = (action: PublicMenuAction) => {
    if (!menu?.id) {
      return;
    }

    void trackPublicMenuEvent({
      menuId: menu.id,
      eventType: 'guest_action_clicked',
      actionTarget: action.href,
      metadata: {
        action_type: action.key,
        business_id: tableSession?.branch.business_id || business?.id || null,
        branch_id: tableSession?.branch.id || branch?.id || null,
        session_id: tableSession?.session.id || null,
        session_type: tableSession ? (isCounterPickupSession ? 'counter' : 'table') : 'public',
        qr_kind: tableSession ? (isCounterPickupSession ? 'counter' : 'table') : 'public',
        table_id: tableSession && !isCounterPickupSession ? tableSession.table.id : null,
        zone_id: tableSession && !isCounterPickupSession ? tableSession.table.zone_id || null : null,
      },
    });
  };

  React.useEffect(() => {
    const handler = (e: CustomEvent<{ mode: string }>) => {
      const mode = customerModes.find((m) => m.key === e.detail.mode);
      if (mode) handleCustomerModeChange(mode);
    };
    window.addEventListener("ai-mode-change", handler as EventListener);
    return () => window.removeEventListener("ai-mode-change", handler as EventListener);
  }, [customerModes, handleCustomerModeChange]);

  // Closed branch → "pre-order for our next opening": ask the server when the
  // branch next opens, then jump straight into the delivery (or pre-order)
  // flow with that time prefilled so the customer's order lands exactly for
  // the moment the branch opens. Delivery is preferred because it carries the
  // "scheduled delivery" meaning the banner advertises; pre-order is the
  // fallback when delivery isn't offered.
  const handleScheduleForNextOpen = useCallback(async () => {
    if (!menu?.id) return;
    setIsSchedulingForNextOpen(true);
    setNextOpenBannerError(null);
    try {
      const res = await fetch(`/api/public/next-open?menuId=${encodeURIComponent(menu.id)}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setNextOpenBannerError('Could not check our opening times. Please try again.');
        return;
      }
      if (!data.nextOpenAt) {
        setNextOpenBannerError('We could not find an upcoming opening time. Please check back later.');
        return;
      }
      const targetMode = customerModes.find((m) => m.key === 'delivery')?.key ?? 'scheduled';
      setNextOpenScheduledTime(data.nextOpenAt);
      setCustomerMode(targetMode as PublicMenuMode['key']);
    } catch {
      setNextOpenBannerError('Could not check our opening times. Please try again.');
    } finally {
      setIsSchedulingForNextOpen(false);
    }
  }, [menu?.id, customerModes]);

  const handleSubmitServiceFeedback = async () => {
    if (isCounterPickupSession) {
      toast.error('Service feedback is only available for dine-in table sessions.');
      return;
    }

    if (!menu?.id || !tableSession?.sessionToken) {
      toast.error('No active table session was found for feedback.');
      return;
    }

    if (feedbackRating < 1 || feedbackRating > 5) {
      toast.error('Choose a rating before sending feedback.');
      return;
    }

    setIsSubmittingFeedback(true);

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      try {
        const { enqueueAction } = await import('@/lib/offline/action-queue');
        await enqueueAction({
          id: crypto.randomUUID(),
          scope: 'menu',
          type: 'submit_service_feedback',
          payload: {
            sessionToken: tableSession.sessionToken,
            rating: feedbackRating,
            feedback: feedbackNote.trim() || undefined,
            guestUsername: guestUsernameForFeedback ?? undefined,
          },
          metadata: { menuId: menu.id, slug },
          createdAt: Date.now(),
          maxAttempts: 3,
        });
        setFeedbackSubmitted(true);
        setFeedbackOpen(false);
        toast.success('Your feedback will be submitted when connection is restored.');
      } catch { toast.error('Could not save your feedback offline.'); }
      setIsSubmittingFeedback(false);
      return;
    }

    try {
      const response = await fetch(resolveApiRequestUrl('/api/public/service-feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken: tableSession.sessionToken,
          rating: feedbackRating,
          feedback: feedbackNote.trim() || undefined,
          guestUsername: guestUsernameForFeedback ?? undefined,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        const message =
          payload && typeof payload === 'object' && 'error' in payload
            ? String((payload as { error?: unknown }).error || 'Could not save your feedback.')
            : 'Could not save your feedback.';
        throw new Error(message);
      }

      setFeedbackSubmitted(true);
      setFeedbackOpen(false);
      void trackPublicMenuEvent({
        menuId: menu.id,
        eventType: 'feedback_submitted',
        actionTarget: tableSession.table.id,
        metadata: {
          business_id: tableSession.branch.business_id,
          branch_id: tableSession.branch.id,
          table_id: tableSession.table.id,
          zone_id: tableSession.table.zone_id || null,
          session_id: tableSession.session.id,
          session_type: 'table',
          qr_kind: 'table',
          rating: feedbackRating,
          has_note: Boolean(feedbackNote.trim()),
        },
      });
      toast.success('Your private service feedback was saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save your feedback.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleCreateServiceRequest = async (
    requestType: 'call_waiter' | 'request_bill' | 'need_water' | 'need_cutlery' | 'need_help',
  ) => {
    if (isCounterPickupSession) {
      toast.error('Service requests are only available for dine-in table sessions.');
      return;
    }

    if (!serviceRequestsEnabled) {
      toast.error('Service requests are not enabled for this table.');
      return;
    }

    if (!menu?.id || !tableSession?.sessionToken) {
      toast.error('No active table session was found.');
      return;
    }

    setActiveServiceRequest(requestType);

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      try {
        const { enqueueAction } = await import('@/lib/offline/action-queue');
        await enqueueAction({
          id: crypto.randomUUID(),
          scope: 'menu',
          type: 'create_service_request',
          payload: { sessionToken: tableSession.sessionToken, requestType },
          metadata: { menuId: menu?.id, slug },
          createdAt: Date.now(),
          maxAttempts: 3,
        });
        toast.success('Your request has been queued and will be sent when connection is restored.');
      } catch { toast.error('Could not save your request offline.'); }
      setActiveServiceRequest(null);
      return;
    }

    try {
      const response = await fetch(resolveApiRequestUrl('/api/public/service-requests'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken: tableSession.sessionToken,
          requestType,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        const message =
          payload && typeof payload === 'object' && 'error' in payload
            ? String((payload as { error?: unknown }).error || 'Could not send this service request.')
            : 'Could not send this service request.';
        throw new Error(message);
      }

      void trackPublicMenuEvent({
        menuId: menu.id,
        eventType: 'service_request_submitted',
        actionTarget: requestType,
        metadata: {
          business_id: tableSession.branch.business_id,
          branch_id: tableSession.branch.id,
          table_id: tableSession.table.id,
          zone_id: tableSession.table.zone_id || null,
          session_id: tableSession.session.id,
          session_type: 'table',
          qr_kind: 'table',
          request_type: requestType,
        },
      });
      toast.success(`${formatServiceRequestLabel(requestType)} sent to staff.`);
      // Briefly show "Sent ✓" on the button before resetting.
      setTimeout(() => setActiveServiceRequest(null), 1500);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send this service request.');
      setActiveServiceRequest(null);
    }
  };

  const handleStartOrderFromFab = () => {
    const firstOrderableItem = document.querySelector('[data-menu-item-click-root="true"] button, [data-menu-item-click-root="true"] a');
    if (firstOrderableItem instanceof HTMLElement) {
      firstOrderableItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    toast('Tap any menu item to add it to your order.');
  };

  const claimablePromotions = useMemo(
    () =>
      canPublicClaimPromotions
        ? (promotions || [])
            .filter((promotion) => promotion.application_mode === 'public_claim' && isPromotionActive(promotion))
            .slice(0, 3)
        : [],
    [canPublicClaimPromotions, promotions],
  );

  // Per-item claimable promotions for the order sheet. Matched here (not in the
  // ordering component) because this is where items carry their category_id.
  const itemPromotionsMap = useMemo(() => {
    if (!canPublicClaimPromotions) return {} as Record<string, Promotion[]>;
    const claimable = (promotions || []).filter(
      (promotion) => promotion.application_mode === 'public_claim' && isPromotionActive(promotion),
    ) as Promotion[];
    if (claimable.length === 0) return {} as Record<string, Promotion[]>;
    const map: Record<string, Promotion[]> = {};
    for (const item of (items || []) as Array<{ id: string; category_id?: string | null }>) {
      const matched = claimable.filter((promotion) => {
        switch (promotion.target_type) {
          case 'item':
            return (promotion.target_ids || []).includes(item.id);
          case 'category':
            return item.category_id ? (promotion.target_ids || []).includes(item.category_id) : false;
          case 'menu':
          case 'business':
            return true;
          default:
            return false;
        }
      });
      if (matched.length) map[item.id] = matched;
    }
    return map;
  }, [canPublicClaimPromotions, promotions, items]);

  // A guest tapping "Sign in to claim" opens the account sheet. This used to
  // dispatch `mnrq:public-menu-shell` for PublicMenuShell's collapsible panel,
  // but the live menu page renders PlaceShell (PublicMenuShell survives only in
  // the preview template), so the event had no listener and the button was
  // dead. The account sheet is the same destination the header's account
  // control opens, so route through the same state.
  //
  // V2 goes straight to the Chow auth modal: its "You" sheet is a profile, not
  // a sign-in wall, and a guest who just tapped "Sign in to checkout" should
  // not have to find the button again inside it.
  const requestCustomerSignIn = useCallback(() => {
    if (experienceV2) {
      setAuthOpen(true);
      return;
    }
    setAccountOpen(true);
  }, [experienceV2]);

  async function handleClaimPromotion(promotion: Promotion) {
    if (!menu?.id || !promotion.id) {
      return;
    }

    if (!branchPosAddonActive || !branchPosFeatures.guestOrdering || !tableSession || isCounterPickupSession || promotion.application_mode !== 'public_claim') {
      toast.error('This promotion is not available for public claim.');
      return;
    }

    setClaimingPromotionId(promotion.id);

    try {
      const response = await fetch(resolveApiRequestUrl('/api/public/promotions/claim'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          promotionId: promotion.id,
          menuId: menu.id,
          path: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message = payload && typeof payload === 'object' && 'error' in payload
          ? String((payload as { error?: unknown }).error || 'Could not claim this promotion.')
          : 'Could not claim this promotion.';
        throw new Error(message);
      }

      setClaimResult({
        claim: (payload as { claim: PromotionClaim }).claim,
        reused: Boolean((payload as { reused?: unknown }).reused),
      });

      void trackPublicMenuEvent({
        menuId: menu.id,
        eventType: 'action',
        actionType: 'promotion_claim',
        actionTarget: promotion.id,
        metadata: {
          promotion_id: promotion.id,
          claim_id: (payload as { claim?: { id?: string } }).claim?.id,
          reused: Boolean((payload as { reused?: unknown }).reused),
        },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not claim this promotion.');
    } finally {
      setClaimingPromotionId(null);
    }
  }

  const copyClaimCode = async () => {
    if (!claimResult?.claim.claim_code || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(claimResult.claim.claim_code);
      toast.success('Claim code copied.');
    } catch {
      toast.error('Could not copy the claim code.');
    }
  };

  const promotionBanner = tableSession && promotionFallbackVisible(shellConfig.promotionFallbackPlacement) && !templateOwnsPromotions && claimablePromotions.length > 0 ? (
    <section className="mx-auto w-full max-w-[980px] px-4 pb-4 pt-4 sm:pb-6 sm:pt-6">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {claimablePromotions.map((promotion) => {
          const isClaiming = claimingPromotionId === promotion.id;
          return (
            <Card
              key={promotion.id}
              className={[
                'shadow-[var(--mnrq-shell-shadow)]',
                promotionPresentation.variant === 'compact-strip' ? 'overflow-hidden' : '',
              ].join(' ')}
              style={{
                borderColor: templateSurfacePalette.promoBorder,
                backgroundColor: templateSurfacePalette.card,
                color: templateSurfacePalette.cardForeground,
              }}
            >
              <CardContent className={promotionPresentation.density === 'compact' ? 'space-y-3 p-4' : 'space-y-4 p-5'}>
                <div className={promotionPresentation.variant === 'compact-strip' ? 'flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between' : 'flex items-start justify-between gap-3'}>
                  <div>
                    <p className="text-2xs font-semibold uppercase tracking-[0.18em]" style={{ color: templateSurfacePalette.mutedForeground }}>Live Offer</p>
                    <h2
                      className={promotionPresentation.variant === 'editorial-card' ? 'mt-2 text-xl font-semibold tracking-tight' : 'mt-2 text-lg font-semibold'}
                      style={{ color: templateSurfacePalette.cardForeground }}
                    >
                      {promotionPresentation.emphasis === 'discount'
                        ? promotion.discount_label || formatPromotionDiscountLabel(promotion, menu?.currency)
                        : promotion.title}
                    </h2>
                    {promotionPresentation.emphasis === 'discount' ? (
                      <p className="mt-1 text-sm" style={{ color: templateSurfacePalette.mutedForeground }}>
                        {promotion.title}
                      </p>
                    ) : null}
                  </div>
                  {promotionPresentation.showBadge ? (
                    <div
                      className="rounded-full border px-3 py-1 text-2xs font-semibold uppercase tracking-[0.12em]"
                      style={{
                        borderColor: templateSurfacePalette.promoBorder,
                        backgroundColor: templateSurfacePalette.promoBg,
                        color: templateSurfacePalette.promoText,
                      }}
                    >
                      {promotion.badge_label || promotion.discount_label || formatPromotionDiscountLabel(promotion, menu?.currency)}
                    </div>
                  ) : null}
                </div>
                {promotionPresentation.showDescription ? (
                  <p className="text-sm" style={{ color: templateSurfacePalette.mutedForeground }}>
                    {promotion.description?.trim() || 'Claim this offer now and show the code to staff when you order.'}
                  </p>
                ) : null}
                {(promotionPresentation.showTarget && promotion.target_label) || (promotionPresentation.showTiming && (promotion.timing_label || promotion.schedule_label)) ? (
                  <div className="flex flex-wrap gap-2 text-2xs uppercase tracking-[0.12em]">
                    {promotionPresentation.showTarget && promotion.target_label ? (
                      <span className="rounded-full border px-3 py-1.5" style={{ borderColor: templateSurfacePalette.border, color: templateSurfacePalette.mutedForeground }}>
                        {promotion.target_label}
                      </span>
                    ) : null}
                    {promotionPresentation.showTiming && (promotion.timing_label || promotion.schedule_label) ? (
                      <span className="rounded-full border px-3 py-1.5" style={{ borderColor: templateSurfacePalette.border, color: templateSurfacePalette.mutedForeground }}>
                        {promotion.timing_label || promotion.schedule_label}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {promotionPresentation.showClaim && promotion.application_mode === 'public_claim' ? (
                  <Button
                    type="button"
                    onClick={() => handleClaimPromotion(promotion)}
                    disabled={isClaiming}
                    className="w-full"
                    style={{
                      backgroundColor: templateSurfacePalette.accent,
                      color: templateSurfacePalette.accentForeground,
                    }}
                  >
                    {isClaiming ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Claiming...
                      </>
                    ) : (
                      <>
                        <TicketPercent className="h-4 w-4" />
                        {promotion.claim_label || 'Claim Offer'}
                      </>
                    )}
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  ) : null;

  const tableSessionBanner = (
    <PublicSessionStatusStrip
      publicCode={tablePublicCode}
      session={tableSession}
      error={tableSessionError}
      isResolving={isResolvingTableSession}
      isCounterPickup={isCounterPickupSession}
      orderingEnabled={branchOrderingEnabled}
      counterRequiresEntryCode={branchOrderingSettings.counterPickupRequireEntryCode}
      counterRequiresGuestName={branchOrderingSettings.counterPickupRequireGuestName}
      activeServiceRequest={activeServiceRequest}
      serviceRequestsEnabled={serviceRequestsEnabled}
      palette={templateSurfacePalette}
      onOrder={handleStartOrderFromFab}
      onFeedback={() => setFeedbackOpen(true)}
      onServiceRequest={(requestType) => void handleCreateServiceRequest(requestType)}
    />
  );

  // ── Place shell inputs ────────────────────────────────────────────────────
  // Everything the chrome needs, derived in one block so there is a single
  // place to look when the header, ribbon or rail is wrong. Before this, each
  // of these was computed inline at its use site and they could disagree.

  // The route root of THIS place, for links the shell builds beneath it. Uses the
  // canonical pair when the URL carries one, and the legacy shape otherwise, so a
  // guest who arrived on a printed code keeps a consistent address bar.
  const placeBasePath = branchSegment && businessSegment
    ? placePath({ business: businessSegment, branch: branchSegment })
    : legacyMenuPath(businessSegment || slug);

  /**
   * V2's branch profile is the standalone Chow page, not the V1 summary sheet.
   * The header's branch control opens that page so there is ONE profile surface
   * wearing the new composition — the sheet's "See everything" link already
   * went there, which made tapping the name feel like a detour.
   */
  const branchAboutHref = experienceV2 && businessSlug && branch?.public_slug
    ? placeSubPath(
        { business: businessSlug, branch: branch.public_slug },
        'about',
        { experience: 'v2' },
      )
    : null;

  const historyBusinessId = tableSession?.branch.business_id || business?.id || menu?.business_id || null;
  const historyParams = new URLSearchParams();
  if (historyBusinessId) historyParams.set('businessId', historyBusinessId);
  const historyBranchId = menu?.branch_id || initialMenu.branch_id || tableSession?.branch.id || null;
  if (historyBranchId) historyParams.set('branchId', historyBranchId);
  const historyBase = businessSlug
    ? placeSubPath({ business: businessSlug }, 'activity', experienceV2 ? { experience: 'v2' } : {})
    : null;
  const orderHistoryHref = experienceV2 && historyBase && historyBusinessId
    ? `${historyBase}${historyBase.includes('?') ? '&' : '?'}${historyParams.toString()}`
    : null;
  const accountParams = new URLSearchParams();
  if (historyBranchId) accountParams.set('branchId', historyBranchId);
  if (experienceV2) accountParams.set('experience', 'v2');
  const accountBase = businessSlug ? placeSubPath({ business: businessSlug }, 'account') : null;
  const accountHref = accountBase
    ? `${accountBase}${accountParams.size ? `?${accountParams.toString()}` : ''}`
    : null;

  // Warm the "You" panel while the guest reads the menu, so opening it paints
  // instead of waiting on three round trips. No-ops for a signed-out guest.
  useCustomerAccountPrefetch({
    enabled: customerSignedIn,
    businessId: tableSession?.branch.business_id || business?.id || menu?.business_id || null,
    fetcher: customerFetch,
  });

  /** The canvas the shell paints over the FULL viewport, so short content never
   *  reveals the platform background underneath the merchant's theme.
   *
   *  Read from the PALETTE, not from templateSurfaceStyle: that helper publishes
   *  `--background` as an HSL triplet custom property and never sets the CSS
   *  `background` shorthand, so reading `.background` off it returns undefined.
   *  The canvas then failed to paint, the menu body fell through to the
   *  platform's light theme, and a dark template's near-white text rendered on
   *  cream — invisible. */
  const placeCanvas = branchTheme?.backgroundPaint?.[publicThemeMode] ?? templateSurfacePalette.background;

  /** The chrome wears the merchant's palette, so the header and rail belong to
   *  the same screen as the menu instead of clamping it between two platform bars. */
  /**
   * doc 64 §8 — the assistant's open/mode state lives here, because the entry
   * is in the header and the panel is a sibling of the menu. The old widget
   * owned its own open state, which is part of why it behaved like a separate
   * product rather than a part of this page.
   */
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantVoice, setAssistantVoice] = useState(false);
  const assistantBusinessId =
    tableSession?.branch.business_id || business?.id || menu?.business_id || '';

  /**
   * Whether to offer the mic at all. Read once on the client: a control that
   * silently does nothing is worse than a control that is not there, and this
   * is false on Firefox and in any context without a microphone.
   */
  const [voiceCaptureAvailable, setVoiceCaptureAvailable] = useState(false);
  /**
   * Held so the tap handler can ASK for the microphone synchronously.
   *
   * A dynamic `import()` inside the handler would spend the guest's user
   * activation waiting on a network round trip, which is the thing we are
   * trying to avoid — so the module is resolved here, at mount, and only
   * called later.
   */
  const askForMicRef = useRef<null | (() => Promise<unknown>)>(null);
  useEffect(() => {
    let cancelled = false;
    /**
     * Loaded SEPARATELY, and that is the point.
     *
     * These were briefly one `Promise.all`, which quietly coupled them: a
     * failure to load the permission helper would have taken the whole
     * microphone button off the header, for a module that only makes the
     * prompt better. Whether voice is OFFERED depends on capture alone.
     */
    void import('@/lib/voice/capture').then((capture) => {
      if (!cancelled) setVoiceCaptureAvailable(capture.isVoiceCaptureSupported());
    }).catch(() => { /* no voice, no problem: chat still works */ });

    void import('@/lib/voice/speech-recognition').then((recognition) => {
      if (!cancelled) askForMicRef.current = recognition.requestMicrophoneAccess;
    }).catch(() => { /* the session asks for the mic itself; only the prompt timing suffers */ });
    return () => { cancelled = true; };
  }, []);

  const placeChromeStyle = useMemo(
    () => buildPlaceChromeStyle(templateSurfacePalette),
    [templateSurfacePalette],
  );

  /** The same palette, mapped for content rather than chrome. */
  const placeBodyStyle = useMemo(
    () => buildPlaceBodyStyle(templateSurfacePalette),
    [templateSurfacePalette],
  );

  /**
   * Can this guest, right now, actually reach a kitchen?
   *
   * `branchOrderingEnabled` is a BRANCH setting and was the wrong gate: a diner
   * whose only mode is dine-in has ordering "enabled" but offers nothing at all
   * to someone who has not scanned a table QR. Gating the Order tab on it
   * produced a tab that led nowhere and add-to-order buttons that did nothing.
   *
   * The reachable test is therefore not the branch's dine-in toggle but whether
   * any non-browse mode is genuinely on the table. `customerModes` already
   * encodes reachability:
   *   - `pickup` requires the counter-pickup setting AND an active pickup
   *     operating mode (session-free) — the island gets `branchOrderingEnabled
   *     = true` for it, so a guest can order from a plain menu link.
   *   - `delivery` requires an active delivery mode with no external href —
   *     also session-free and unconditionally enabled in the island mount.
   *   - `scheduled` likewise.
   *   - `dine_in` ONLY appears when a live table session exists, so it can
   *     never offer "ordering" to someone who has not scanned a QR.
   * So a mode present in `customerModes` means ordering works, and one absent
   * means it cannot. That read makes orderingReachable true for a pickup- or
   * delivery-capable branch (the Order tab + per-row add appear) and false for
   * a browse-only or unscanned-dine-in branch (nothing dead to offer).
   */
  const orderingReachable = customerModes.some((mode) => mode.key !== 'menu');

  /**
   * Sub-typed standard branches render `SubTypeCustomerFlow` instead of the
   * generic ordering island, and that router has no `initialItemToOpen` prop —
   * so an item handed to it is silently dropped.
   *
   * Until those five flow components can accept an item, the menu body must not
   * offer a per-item add button on these branches: it would look identical to
   * the working one and do nothing. The Order tab still works (it switches mode
   * and opens the flow), so ordering stays reachable — just not per row.
   */
  /**
   * The sub-type customer flows (QuickOrderFlow, RestaurantOrderFlow, …) were
   * once mounted as the shell's whole BODY, so choosing an ordering mode on a
   * `standard` branch with a specific_type swapped the merchant's template menu
   * for a different renderer — the menu visibly changed with the mode. That is
   * not how a template should behave: a template is assigned to a branch and
   * displays the SAME menu whatever mode the guest is in. So these flows sit
   * unused here; ordering routes through the generic ordering island
   * (PublicMenuOrderingExperience), which is overlay-based and leaves the
   * template in place. `usesSubTypeOrderFlow` is thus always false, which also
   * re-enables the per-row "add" affordance on these branches.
   */
  const usesSubTypeOrderFlow = false;

  const placeCapabilities = useMemo(
    () => ({
      businessType: branch?.business_type ?? null,
      specificType: branch?.specific_type ?? null,
      ordering: orderingReachable,
      reservations: reserveEnabled,
      booking: getBusinessType(branch?.business_type).presentation.customerEntry === 'booking',
      bookingHref,
      reservationHref: reserveHref,
    }),
    [branch?.business_type, branch?.specific_type, orderingReachable, reserveEnabled, bookingHref, reserveHref],
  );

  /**
   * The place's other destinations — Book / Reserve, and the guest's own
   * account. These used to BE the bottom rail; the rail is gone (see
   * components/place/PlaceActionBar.tsx) and they are now reached from the
   * header and the You sheet, so this is a capability list rather than chrome.
   */
  const placeDestinations = useMemo(
    () => resolvePlaceTabs(placeCapabilities),
    [placeCapabilities],
  );

  /** Where a guest can book or reserve at this branch, if anywhere. Surfaced in
   *  the You sheet, and — on a place whose whole business is bookings — as the
   *  action bar's standing invitation. */
  const bookDestination = useMemo(
    () => placeDestinations.find((destination) => destination.key === 'book') ?? null,
    [placeDestinations],
  );

  /**
   * A caterer or events venue has no cart to fill, so its action bar would
   * otherwise never render at all. Give it the one thing its guests are there
   * to do, permanently. A branch that takes orders gets null: its bar belongs
   * to the cart, and a standing "Reserve a table" button competing with a live
   * order is exactly the two-things-at-once noise being removed.
   */
  const placeInvite = useMemo(() => {
    if (orderingReachable) return null;
    const primary = placePrimaryAction(placeCapabilities);
    if (!primary) return null;
    return { label: primary.label, href: primary.href, lead: branch?.name ?? null };
  }, [orderingReachable, placeCapabilities, branch?.name]);

  /**
   * "How do you want this?" — passed to the action bar ONLY while the guest has
   * items and has not settled the question. See `fulfilmentAnswered`.
   */
  const fulfilmentPrompt = useMemo(() => {
    if (!fulfilmentChoiceExists || fulfilmentAnswered) return null;
    // A scanned table already answered it; re-asking invites the guest to
    // contradict the seat they are sitting in.
    if (tableSession) return null;
    return {
      // Plain words, and a QUESTION rather than a label. The retired chip row
      // said "Ordering mode" to a screen reader and nothing at all to a guest.
      question: 'How would you like this?',
      options: selectableModes.map<PlaceActionOption>((mode) => ({
        key: mode.key,
        label: mode.label,
        hint: mode.description ?? null,
      })),
    };
  }, [
    fulfilmentChoiceExists,
    fulfilmentAnswered,
    tableSession,
    selectableModes,
    branch?.business_type,
    branch?.specific_type,
  ]);

  /**
   * What does this order need from the guest before checkout? (doc 71 MNRQ-UX-03)
   *
   * Decided HERE because this is the only place that knows both the mode and
   * whether a QR was scanned. The rule is `resolveFulfilmentPolicy`, shared
   * with the server gate so the button and the boundary cannot drift:
   *
   *   · pickup / scheduled  → guest-first: a name + reachable contact, no
   *     account (unless the merchant opted into mandatory identity).
   *   · delivery            → account-required, unchanged.
   *   · dine-in / counter   → nothing.
   *
   * The `tableSession` guard is the important half. A counter/till QR sets
   * `customerMode` to `'pickup'` and renders the ordering island with
   * `tableSession={null}`, so from inside that island a guest standing at the
   * till looks exactly like one ordering from home. Without this guard the gate
   * would have demanded a sign-in from someone already holding their wallet at
   * the counter. `scannedQr` carries that fact into the policy.
   */
  const fulfilmentPolicy = useMemo(
    () =>
      resolveFulfilmentPolicy({
        mode: toOrderContextMode(customerMode),
        scannedQr: Boolean(tableSession),
      }),
    [tableSession, customerMode],
  );
  const accountRequiredForCheckout = fulfilmentPolicy.requiresAccount;
  const contactRequiredForCheckout = fulfilmentPolicy.requiresContact;

  /**
   * The arrival question — doc 70 §3, and the owner's clearest ask: "when I
   * come, I just choose which mode I want. Simple as that."
   *
   * WHY THIS IS SAFE TO MOUNT NOW, when the previous arrival modal was removed.
   * That one stranded people: it listed every mode including a "Browse" that was
   * selected by default, and a guest who dismissed it — most of them — was left
   * in a mode where nothing on the menu could be added to a cart. Three things
   * are different here:
   *
   *   1. It is only mounted when there is genuinely more than one way to get
   *      food (`fulfilmentChoiceExists`), and never over a scanned QR session.
   *   2. Browse is offered explicitly as "Just looking", and is never the
   *      default answer.
   *   3. DISMISSING CHANGES NOTHING. `fulfilmentAnswered` stays false, so the
   *      page keeps the best real ordering mode it already resolved and the
   *      action bar asks again at the moment the first item lands in the cart —
   *      which is exactly today's behaviour. The sheet can only add an answer,
   *      never take the guest somewhere worse.
   */
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const arrivalShownRef = useRef(false);

  useEffect(() => {
    if (arrivalShownRef.current) return;
    // Chow never asks a logistics question before the guest has looked at the
    // menu. V2 starts in the best real ordering mode and asks at checkout.
    if (experienceV2) return;
    // Never over a scanned seat or till: that QR already answered the question.
    //
    // BOTH checks are required, and the URL one is the load-bearing half. The
    // session is resolved ASYNCHRONOUSLY, so on the first render after a QR scan
    // `tableSession` is still null — the effect fired, the sheet opened, the ref
    // latched, and a guest sitting at Table 1 was asked "what are you doing
    // today?" with the answer already printed on the code they had just scanned.
    // Caught by driving a real table QR; it is invisible to any test that starts
    // from a plain menu URL.
    //
    // `table_code`/`zone_code` in the URL is the intent, and it is known
    // synchronously on the very first render, so it closes the race the session
    // lookup opens.
    if (tablePublicCode || zoneCode || qrScanEnabled) return;
    if (tableSession) return;
    // Nothing to ask when the branch has one way (or no way) to serve them.
    // Destinations count as genuine choices too: a branch with a single
    // ordering mode plus "Reserve a table" still has more than one answer to
    // "what are you doing today?" (MNRQ-UX-02).
    if (!fulfilmentChoiceExists && arrivalDestinations.length === 0) return;
    if (fulfilmentAnswered) return;
    // Wait until the modes have actually resolved, so the sheet never flashes
    // up with a single option and then grows another.
    if (selectableModes.length + arrivalDestinations.length < 2) return;
    arrivalShownRef.current = true;
    setArrivalOpen(true);
  }, [experienceV2, tableSession, tablePublicCode, zoneCode, qrScanEnabled, fulfilmentChoiceExists, fulfilmentAnswered, selectableModes.length, arrivalDestinations.length]);

  const handleChooseFulfilment = useCallback(
    (key: string) => {
      // Reads from `selectableModes`, not `orderModes`, so "Just looking" is a
      // reachable answer rather than a silently ignored tap.
      const mode = selectableModes.find((m) => m.key === key);
      if (!mode) return;

      // Doc 70 §3: say what the switch costs BEFORE making it.
      //
      // The cart is scoped by BRANCH, not by mode (`cartScopeId` in
      // PublicMenuOrderingExperience), so switching Delivery → Pickup used to
      // silently re-label the existing cart and drop the guest into a checkout
      // whose address they had entered for a mode they were no longer in.
      // Nothing warned them.
      //
      // The message is phrased from `drops` alone and says nothing about how
      // many items are in the cart: the cart lives inside the ordering island,
      // which reports its count to PlaceProvider — and this component is the
      // provider's PARENT, so it cannot read it. Rather than thread a count up
      // just to decorate a sentence, the warning states only what is certainly
      // true (the items survive; these details stop applying).
      const effect = describeModeChange({
        from: toOrderContextMode(customerMode),
        to: toOrderContextMode(mode.key),
        // Not the real count — see above. Only `drops` is consumed here.
        cartCount: 0,
      });
      const dropNote = effect.drops.includes('address')
        ? 'Your items are kept. The delivery address you entered won’t apply to this order.'
        : effect.drops.includes('slot')
        ? 'Your items are kept. The pickup time you chose won’t apply to this order.'
        : effect.drops.includes('seat')
        ? 'Your items are kept, but this order will no longer be sent to your table.'
        : null;
      if (dropNote) toast(dropNote);

      // Switching from the checkout remounts the ordering island for the new
      // mode, which would otherwise close the sheet. Flag both so the guest
      // lands back on the step they were on with their cart intact.
      if (experienceV2 && orderingCartOpen && (key === 'pickup' || key === 'delivery')) {
        setPendingCartOpen(true);
        setPendingCheckoutOpen(true);
      }

      setFulfilmentAnswered(true);
      handleCustomerModeChange(mode);
    },
    [selectableModes, handleCustomerModeChange, customerMode, experienceV2, orderingCartOpen],
  );

  /**
   * The clause the action bar appends to the cart total, so the guest can see
   * how the food is coming without a second element saying it. This is where
   * the retired mode chip row's information actually went.
   */
  const cartFulfilmentDetail = useMemo(() => {
    if (tableSession && !isCounterPickupSession) return `to ${tableSession.table.label}`;
    switch (customerMode) {
      case 'pickup':
        return 'for pickup';
      case 'delivery':
        return 'for delivery';
      case 'scheduled':
        return 'pre-order';
      case 'dine_in':
        return 'to your table';
      default:
        return null;
    }
  }, [customerMode, tableSession, isCounterPickupSession]);

  /**
   * The order this device is waiting on, if any. Polled from the guest's own
   * stored orders — see hooks/menu/useLivePlaceOrder.ts.
   *
   * This was the missing input. `resolvePlaceState` could always describe a
   * live order and an unpaid one, and the action bar could always render both,
   * but `placeSituation` never passed an `order`, so a guest who placed an
   * order watched the bar go straight back to empty. The shell forgot about the
   * food the moment it was ordered.
   */
  // Guest order records store the public business segment from `/m/{business}`.
  // On a branch route `menu.slug` can instead be the selected branch's menu,
  // which made a just-placed order invisible to this menu's live-order shortcut.
  const livePlaceOrder = useLivePlaceOrder(businessSegment ?? menu?.slug ?? slug);

  /** Everything the shell's state is derived from. The cart half is filled in
   *  by PlaceProvider, which receives it from the ordering island. */
  const placeSituation = useMemo(
    () => ({
      branch: branch
        ? { operating_hours: branch.operating_hours, timezone: branch.timezone }
        : null,
      session: tableSession
        ? {
            table: {
              label: tableSession.table.label,
              is_counter_pickup: tableSession.table.is_counter_pickup,
            },
            session: { status: tableSession.session.status },
          }
        : null,
      order: livePlaceOrder,
      mode: customerMode,
      // The header's status line becomes the mode switcher whenever the branch
      // offers more than one way to get food. A seated guest's mode is decided
      // by the QR they scanned.
      modeChangeAvailable: Boolean(fulfilmentChoiceExists && !tableSession),
    }),
    [branch, tableSession, livePlaceOrder, customerMode, fulfilmentChoiceExists],
  );

  /** Rendered before PlaceProvider resolves the cart, so the ribbon is correct
   *  on the server render too rather than flashing in. */
  const placeState = useMemo(() => resolvePlaceState(placeSituation), [placeSituation]);

  /** Menu structure for the nav strip. `null` is the "everything" chip. */
  const placeNavGroups = useMemo(
    () =>
      collectionViews.length > 1
        ? [
            { id: null, label: 'All' },
            ...collectionViews.map((c) => ({ id: String(c.id), label: c.name || 'Menu' })),
          ]
        : [],
    [collectionViews],
  );

  /** The menu grouped into its own sections, for Menuraq's menu body. Built
   *  from the SAME rendererData the templates receive, so a category filter, a
   *  search term and a translation all apply here without a second code path. */
  const placeMenuSections = useMemo(() => {
    const items = (rendererData?.menu?.items || []) as OrderingMenuItem[];
    const categories = (rendererData?.menu?.categories || []) as Array<{
      id: string;
      name?: string;
      description?: string;
    }>;

    const byCategory = new Map<string, OrderingMenuItem[]>();
    for (const item of items) {
      const key = String((item as { category_id?: string }).category_id || item.category || '__loose__');
      const bucket = byCategory.get(key);
      if (bucket) bucket.push(item);
      else byCategory.set(key, [item]);
    }

    const sections = categories.map((category) => ({
      id: String(category.id),
      name: category.name || 'Menu',
      description: category.description || null,
      items: byCategory.get(String(category.id)) || [],
    }));

    // Items whose category was deleted or never set would otherwise vanish from
    // the menu entirely — invisible to the guest and to the merchant.
    const claimed = new Set(sections.flatMap((section) => section.items.map((item) => item.id)));
    const orphans = items.filter((item) => !claimed.has(item.id));
    if (orphans.length > 0) {
      sections.push({ id: '__more__', name: 'More', description: null, items: orphans });
    }

    return sections;
  }, [rendererData]);

  const placeNavSections = useMemo(
    () => [
      { id: null, label: 'All', count: placeMenuSections.reduce((n, s) => n + s.items.length, 0) || undefined },
      ...navigationCategories.map((c) => {
        const matching = placeMenuSections.find((s) => String(s.id) === String(c.id));
        return { id: String(c.id), label: c.name || 'Section', count: matching?.items.length || 0 };
      }),
    ],
    [navigationCategories, placeMenuSections],
  );

  /** How many items the current search term matches. Counted from the same
   *  already-filtered data the menu body renders, so the number and the list can
   *  never disagree. Undefined when nothing is being searched. */
  const searchResultCount = useMemo(() => {
    if (!searchQuery.trim()) return undefined;
    const items = presentedMenuData?.menu?.items || effectiveMenuData?.menu?.items;
    return Array.isArray(items) ? items.length : undefined;
  }, [searchQuery, presentedMenuData, effectiveMenuData]);

  /**
   * The sub-type ordering flow, rendered as the shell's BODY.
   *
   * These flows (QuickOrderFlow, RestaurantOrderFlow, …) are complete
   * experiences: they render their own full menu, cart and checkout. They used
   * to mount *below* `</PlaceShell>`, so the moment a guest picked a fulfilment
   * mode on a sub-typed branch the page showed the menu TWICE — the Place
   * shell's list, then the flow's own list underneath it, past the bottom rail.
   *
   * A flow that owns the whole experience has to BE the body, not sit under it.
   * Null in browse mode, and on branches that use the generic ordering island
   * (which is overlay-based and correctly stays where it is).
   */
  const subTypeFlowBody = useMemo(() => {
    if (!usesSubTypeOrderFlow || customerMode === 'menu' || !SubTypeCustomerFlow) return null;
    if (!branch?.specific_type) return null;
    return (
      <div style={templateSurfaceStyle}>
        <SubTypeCustomerFlow
          branchId={branch.id || ''}
          menuId={menu?.id || ''}
          specificType={branch.specific_type}
          initialMenu={initialMenu}
          orderMode={customerMode === 'dine_in' ? 'dine_in' : customerMode === 'delivery' ? 'delivery' : customerMode === 'scheduled' ? 'scheduled' : 'pickup'}
          sessionToken={customerMode === 'dine_in' ? tableSession?.session?.id || undefined : undefined}
          onOrderPlaced={() => {
            void loadMenuData(true);
          }}
        />
      </div>
    );
  }, [usesSubTypeOrderFlow, customerMode, branch?.specific_type, branch?.id, menu?.id, initialMenu, templateSurfaceStyle, tableSession?.session?.id, loadMenuData]);

  // The menu body only. PlaceShell owns the page's <main>, the chrome offsets
  // and the bottom clearance for the rail — this used to carry a hardcoded
  // `max-md:pt-20 pb-36` to dodge chrome it did not own, which is why the
  // spacing drifted every time the shell changed.
  const rendererContent = (
    <div
      className="w-full"
      data-menu-item-click-root="true"
      ref={pullRef}
      // The merchant's palette and type, scoped to the BODY. The chrome around
      // it takes the same palette via buildPlaceChromeStyle but keeps the
      // platform's control face, so a "Continue" button never renders in a
      // decorative display font. This style also publishes
      // --mnrq-template-font-heading, which Menuraq's own menu body reads.
      //
      // buildPlaceBodyStyle re-declares the semantic colour tokens against the
      // CANVAS this content sits on. Without it the body inherits the chrome's
      // tokens from the shell root — which is how a dark template ended up
      // painting near-white dish names onto its own cream menu.
      style={{ ...templateSurfaceStyle, ...placeBodyStyle }}
    >
        {/* Pull-to-refresh indicator */}
        {(pullState === 'pulling' || pullState === 'refreshing') && (
          <div className="pointer-events-none flex items-center justify-center" style={{ height: Math.max(pullDistance, 24) }}>
            {pullState === 'refreshing' ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <ArrowDown
                className="h-5 w-5 text-muted-foreground transition-transform duration-150"
                style={{ transform: `rotate(${Math.min(pullDistance / 80, 1) * 180}deg)` }}
              />
            )}
          </div>
        )}
      {tableSessionBanner}
      {/* Wallet-aware moment (doc 08 §7.3 / F2): a signed-in customer is
          reminded of the gift card balance and loyalty points they hold at
          this place. Awareness only , nothing is applied automatically. */}
      {!isEmbeddedView && (tableSession?.branch.business_id || business?.id || menu?.business_id) ? (
        <WalletMoment
          businessId={tableSession?.branch.business_id || business?.id || menu?.business_id || ''}
          businessName={businessInfo.name}
          palette={templateSurfacePalette}
        />
      ) : null}
      {shellConfig.promotionFallbackPlacement === 'before-template' ? promotionBanner : null}
      {/* Two bodies, one shell — the fork, the error state and the skeleton
          live in `components/place/PlaceMenuBody.tsx`. */}
      <div key={activeView} className={experienceV2 ? 'gv-menu-view-swap' : undefined}>
      <PlaceMenuBody
        showTemplateBody={showTemplateBody}
        experienceV2={experienceV2}
        template={resolvedTemplateSchema}
        data={rendererData}
        themeMode={publicThemeMode}
        loadError={loadError}
        onRetry={() => setReloadNonce((value) => value + 1)}
        loadingPalette={loadingPalette}
        sections={placeMenuSections}
        layout={experienceV2 ? v2StorefrontLayout : storefrontLayout}
        dietaryProfile={dietaryProfile}
        hideDietaryClashes={hideDietaryClashes}
        searchQuery={searchQuery}
        onClearSearch={() => {
          setSearchQuery('');
          setSearchOpen(false);
        }}
        emptyMessage={
          hideDietaryClashes ? 'Nothing here matches your dietary filters.' : undefined
        }
        onOpenItem={(item) => {
          if (experienceV2) {
            // Use the shared details-first item flow for orderable V2 items.
            // The next step contains modifier choices only when applicable.
            if (orderingReachable && !usesSubTypeOrderFlow) {
              if (customerMode === 'menu') {
                const first = customerModes.find((mode) => mode.key !== 'menu');
                if (first) handleCustomerModeChange(first);
              }
              setBrowseOrderItem(item);
              return;
            }
            // Branches that do not allow ordering keep a read-only item sheet.
            setBrowseDetailItem(item);
            setBrowseDetailOpen(true);
            return;
          }
          if (orderingReachable && !usesSubTypeOrderFlow) {
            if (customerMode === 'menu') {
              const first = customerModes.find((mode) => mode.key !== 'menu');
              if (first) handleCustomerModeChange(first);
            }
            setBrowseOrderItem(item);
            return;
          }
          setBrowseDetailItem(item);
          setBrowseDetailOpen(true);
        }}
        // Only when an order can actually be placed from here.
        onAddItem={
          orderingReachable && !usesSubTypeOrderFlow
            ? (item) => {
                const optionGroups = (item as any)?.option_groups ?? [];
                const hasOptions = Array.isArray(optionGroups) && optionGroups.length > 0;
                if (experienceV2) {
                  // Open the Chow item screen directly; it renders the option
                  // choices inline without a preview-to-customize handoff.
                  if (hasOptions) {
                    setBrowseOrderItem(item);
                    return;
                  }
                  if (customerMode === 'menu') {
                    const first = customerModes.find((mode) => mode.key !== 'menu');
                    if (first) handleCustomerModeChange(first);
                  }
                  try {
                    if (typeof window !== 'undefined') {
                      window.sessionStorage.setItem('mnrq_v2_quick_add', JSON.stringify({ qty: 1, note: '' }));
                    }
                  } catch {}
                  setBrowseOrderItem(item);
                  return;
                }
                // If item has options/add-ons, open the details sheet so guest can customize.
                if (hasOptions) {
                  setBrowseDetailItem(item);
                  setBrowseDetailOpen(true);
                  return;
                }
                if (customerMode === 'menu') {
                  const first = customerModes.find((mode) => mode.key !== 'menu');
                  if (first) handleCustomerModeChange(first);
                }
                setBrowseOrderItem(item);
              }
            : undefined
        }
      />
      </div>
      {shellConfig.promotionFallbackPlacement !== 'before-template' ? promotionBanner : null}

      {/* Closed-branch scheduling prompt: branch hours configured + currently
          closed + an in-app ordering mode to land in → offer to pre-order for
          the next opening instead of dead-ending the customer. */}
      {branchOpenNow === false && (customerModes.some((m) => m.key === 'delivery') || customerModes.some((m) => m.key === 'scheduled')) && (
        <div
          className="mx-auto my-3 flex w-full max-w-[980px] flex-col gap-2 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          style={{
            borderColor: templateSurfacePalette.border,
            backgroundColor: templateSurfacePalette.card,
            color: templateSurfacePalette.cardForeground,
          }}
        >
          <div className="flex items-start gap-2.5">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />
            <div>
              <p className="text-sm font-semibold">We're closed right now</p>
              <p className="text-xs opacity-70">
                Place an order now and we'll get it ready the moment we open.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSchedulingForNextOpen}
            onClick={() => void handleScheduleForNextOpen()}
          >
            {isSchedulingForNextOpen ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              'Pre-order for when we open'
            )}
          </Button>
          {nextOpenBannerError && (
            <p className="text-xs text-destructive sm:max-w-[180px] sm:text-right">{nextOpenBannerError}</p>
          )}
        </div>
      )}
      {isHydrating && rendererData ? (
        <div
          className="pointer-events-none fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-full border px-3 py-1 text-xs shadow-sm backdrop-blur max-sm:bottom-36 sm:bottom-3"
          style={{
            borderColor: templateSurfacePalette.border,
            backgroundColor: templateSurfacePalette.card,
            color: templateSurfacePalette.mutedForeground,
          }}
        >
          Syncing latest menu updates...
        </div>
      ) : null}
    </div>
  );

  const claimStatusMessage = claimResult?.claim.status === 'redeemed'
    ? 'This code has already been redeemed for this session.'
    : claimResult?.reused
      ? 'You already claimed this offer on this device. Use the same code with staff.'
      : 'Your offer is ready. Show this code to staff when you order.';

  const handleZoneTableSelected = useCallback(
    (tablePublicCode: string) => {
      const url = new URL(window.location.href);
      url.searchParams.set('table_code', tablePublicCode);
      url.searchParams.delete('zone_code');
      window.location.href = url.toString();
    },
    [],
  );

  if (zoneCode && !tablePublicCode) {
    return (
      <ZonePickerShell
        zoneCode={zoneCode}
        menuId={menu?.id || ''}
        onTableSelected={handleZoneTableSelected}
      />
    );
  }

  // An embedded menu (iframe on a merchant's own site) gets the body and nothing
  // else — a shell with a bottom rail inside someone else's page is chrome the
  // host did not ask for, and the rail's fixed positioning would escape the frame.
  if (isEmbeddedView) {
    return (
      <div className="min-h-screen w-full" style={templateSurfaceStyle}>
        {rendererContent}
        <PublicMenuPoweredFooter
          brandingLevel={brandingLevel}
          visible={footerVisible(shellConfig.footerStyle)}
          palette={templateSurfacePalette}
        />
      </div>
    );
  }

  return (
    <PlaceProvider basePath={placeBasePath} situation={placeSituation}>
      {/* Full-page delivery checkout (doc 74 / delivery-app flow). When the
          guest taps "Review order" in delivery mode, PublicMenuOrderingExperience
          swaps its own island for DeliveryCheckoutSurface — but that island
          renders AFTER this shell, so the menu body would otherwise stay
          visible above the checkout, which is the "blank Delivery Checkout at
          the bottom of the page" bug. Hiding the whole shell makes the swap
          real: the surface is the only thing on screen, with its own header,
          back button and step rail. */}
      {deliveryCheckoutOpen && customerMode === 'delivery' ? null : (
      <PlaceShell
        basePath={placeBasePath}
        experienceVersion={experienceV2 ? 'v2' : 'v1'}
        className={
          experienceV2
            ? 'place-experience-v2 chow-root guest-v2'
            : undefined
        }
        lineTabs={
          <PlaceLineTabs tabs={lineTabs} activeSlug={activeLineSlug} basePath={placeBasePath} />
        }
        // Layout is shared; colour identity belongs to the effective merchant theme.
        canvas={placeCanvas}
        chromeStyle={{ ...guestPaletteStyle, ...placeChromeStyle }}
        measure={
          experienceV2
            ? '1240px'
            : storefrontLayout === 'cards'
              ? '64rem'
              : storefrontLayout === 'showcase'
                ? '52rem'
                : '48rem'
        }
        state={placeState}
        fulfilment={
          experienceV2 && fulfilmentChoiceExists && (!tableSession || isCounterPickupSession)
            ? null
            : fulfilmentPrompt
        }
        onChooseFulfilment={handleChooseFulfilment}
        // The header's status-line mode picker. Same options as the action
        // bar's fulfilment prompt, so the guest can change modes before adding
        // items — the bar only asks once the cart has something in it.
        modes={selectableModes.map((mode) => ({
          key: mode.key,
          label: mode.label,
          hint: mode.description ?? null,
        }))}
        onModeChange={handleChooseFulfilment}
        currentModeKey={customerMode === 'menu' ? null : customerMode}
        // The persistent "change mode" control in the header's second line.
        // Shown whenever the branch offers more than one way to get food and
        // the guest has picked one — independent of the cart, so the choice can
        // always be revisited. A seated guest's mode is decided by the QR they
        // scanned, so it is not offered here.
        modeSwitcher={
          // Shown in BROWSE too (doc 70 §3). It used to be hidden whenever
          // `customerMode === 'menu'`, which was harmless while browse was
          // unreachable — but browse is now something a guest can deliberately
          // choose ("Just looking"), and hiding the switcher there left them with
          // no way back to ordering except by tapping a dish and noticing the
          // "Order now" button inside its sheet. A guest who chose to look
          // around must be able to change their mind from the same control that
          // every other mode uses.
          // A COUNTER QR is different from a table: the guest is not seated, and the
          // stored session outlives the visit by hours, so hiding the switcher for it
          // locked a phone that had once scanned the till into pickup — delivery was
          // unreachable on mobile while the same menu on desktop (no session) worked.
          fulfilmentChoiceExists && (!tableSession || isCounterPickupSession)
            ? customerMode === 'menu'
              ? { label: 'Just looking', detail: 'tap to start an order' }
              : isCounterPickupSession && customerMode === 'pickup'
              ? { label: 'At the counter', detail: 'order here, collect at the till' }
              : {
                  label: MODE_WORDS[customerMode]?.label ?? customerMode,
                  detail: MODE_WORDS[customerMode]?.detail ?? null,
                }
            : null
        }
        invite={placeInvite}
        cartDetail={cartFulfilmentDetail}
        orderNoun={midSentence(word(branch?.business_type, 'order', branch?.specific_type))}
        // The bar and an expanded cart sheet are the same conversation; showing
        // both puts a "Review order" button on top of the order being reviewed.
        // The full-page delivery checkout closes that sheet, so it suppresses
        // the bar the same way.
        barSuppressed={orderingCartOpen || deliveryCheckoutOpen}
        branchDetails={experienceV2 && branchAboutHref ? undefined : publicBranchDetails}
        isOpenNow={branchOpenNow}
        // Reviews and the branch's own page live in the branch sheet now — see
        // components/place/BranchInfoSheet.tsx for why they moved off the menu.
        branchId={branch?.id ?? null}
        businessName={businessInfo.name}
        placePageHref={branchAboutHref ?? (menu?.slug ? `/b/${menu.slug}` : null)}
        // `?choose=1` suspends the resolver's remembering rungs for one render
        // (see resolvePlace), which is what makes this a way OUT of the branch
        // the guest is in rather than a link straight back to it.
        switchBranchHref={
          businessSlug && (siblingBranchCount ?? 0) > 1
            ? placePath(
                { business: businessSlug },
                experienceV2 ? { experience: 'v2', choose: true } : { choose: true },
              )
            : null
        }
        header={{
          title: branch?.name || businessInfo.name,
          subtitle: branch?.name ? businessInfo.name : menu?.title || null,
          // V2 opens the standalone Chow branch page; V1 keeps its sheet.
          // PlaceShell prefers `branchDetails` when it is present, so this only
          // takes effect once the V2 page has replaced that sheet.
          onBranchInfo: branchAboutHref
            ? () => {
                window.location.assign(branchAboutHref);
              }
            : undefined,
          // The business logo fills the header's leading slot (there is no back
          // link on the menu landing), so a guest who scanned a QR recognises
          // whose menu this is before reading the title.
          logoUrl: typeof businessInfo.logo_url === 'string' && businessInfo.logo_url ? businessInfo.logo_url : null,
          onSearch: subTypeFlowBody
            ? undefined
            : () => {
                if (!experienceV2) {
                  setSearchOpen(true);
                  return;
                }
                // One control, two states, exactly like the app's header: the
                // filter icon opens the filter sheet; closing it keeps applied
                // filters and the query so results remain predictable.
                if (searchOpen) {
                  setSearchOpen(false);
                  return;
                }
                setSearchOpen(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              },
          searchOpen: experienceV2 ? searchOpen : false,
          // The account control lives HERE now. It used to be the rail's "You"
          // tab and was deliberately kept out of the header to avoid two
          // controls with one destination; with the rail retired this is the
          // one place it exists, and the corner is exactly where a guest looks
          // for "my stuff".
          onAccount: () => setAccountOpen(true),
          /**
           * Language moved back into the You sheet.
           *
           * It used to live in this row because a guest who cannot read the
           * menu cannot read the word "You" either. That is still true, so the
           * sheet does not hide it behind an English label: the row carries a
           * globe, the current language is shown in its own script, and the
           * list expands inline with every language written natively. What
           * changed is the header budget — three 44px controls plus the title
           * is more chrome than a single-screen menu can afford, and language
           * is a setting, not a destination. Search (the filter icon) and You
           * stay in the corner.
           *
           * V1 still renders the globe below: its account sheet is the old
           * one, so hiding it there would strand a guest on a menu they cannot
           * read.
           */
          trailing: (
            <div className="flex items-center gap-1">
              {/* The menu-view toggle — the web twin of the Chow app's
                  design/list switch. It sits with the assistant rather than
                  in the icon row because it is a MODE, not a destination. */}
              <Suspense fallback={<div className="size-11" />}>
                <PlaceMenuViewSwitcher
                  view={activeView}
                  canShowDesign={canShowDesign}
                  designLabel={template?.name ?? null}
                  experienceV2={experienceV2}
                  onChangeView={changeMenuView}
                />
              </Suspense>
              {/* doc 64 D9 — the assistant lives in the header now. It used to be
                  a bubble floating over the bottom-right corner, which is where
                  the guest's ORDER belongs; it sat on top of the last row's
                  add-to-order button and read as a chat product bolted onto a
                  menu rather than part of the place. */}
              {assistantBusinessId ? (
                <AiEntryPill
                  onOpenChat={() => { setAssistantVoice(false); setAssistantOpen(true); }}
                  onOpenVoice={
                    customerVoiceEnabled() && voiceCaptureAvailable
                      ? () => {
                          /**
                           * iOS Safari only allows `speechSynthesis.speak()` if
                           * the FIRST call happens inside a user gesture. Our
                           * first real call is a reply arriving on a network
                           * callback seconds later, so on iPhone the assistant
                           * listened, thought, and then said nothing at all. A
                           * silent utterance here — during the tap itself —
                           * unlocks it for the rest of the session.
                           */
                          try {
                            const synth = window.speechSynthesis;
                            if (synth) {
                              const silent = new SpeechSynthesisUtterance('');
                              silent.volume = 0;
                              synth.speak(silent);
                              synth.cancel();
                            }
                          } catch { /* captions still carry the reply */ }

                          /**
                           * ASK for the microphone here, during the tap.
                           *
                           * The voice session also asks, but it does so from an
                           * effect that runs after the tap has left the stack —
                           * so the request carries no user activation. Chrome
                           * treats an unprompted request as a candidate for its
                           * quiet UI and can resolve it without ever showing the
                           * guest a choice, which is exactly the reported bug:
                           * the panel opened straight into "your microphone is
                           * blocked" having never asked. Asking on the gesture
                           * makes the prompt the browser's normal, loud one.
                           *
                           * Idempotent: once granted, the session's own call
                           * resolves immediately and no second prompt appears.
                           */
                          void askForMicRef.current?.();

                          setAssistantVoice(true);
                          setAssistantOpen(true);
                        }
                      : undefined
                  }
                />
              ) : null}
              {/* V1 keeps the globe in the header. Its account sheet is the
                  old one and has no language section, so removing the control
                  from the shared header would leave V1 guests with no way to
                  read the menu in their language. V2's copy lives in the You
                  sheet. */}
              {!experienceV2 ? (
                <MenuLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onSelect={setSelectedLanguage}
                  isTranslating={isTranslating}
                  compact
                />
              ) : null}
            </div>
          ),
        }}
      >
        {/* The shell's menu navigation steps aside when a sub-type flow owns the
            body: those flows ship their own search field and their own category
            chips, so rendering both stacked two identical category rows and two
            search affordances on top of each other. The header is chrome, not
            menu navigation, so it stays. */}
        {subTypeFlowBody ? null : experienceV2 ? (
          <GuestV2MenuNav
            title={businessInfo.name || branch?.name || 'This place'}
            view={activeView}
            canShowDesign={canShowDesign}
            isBook={templateEngine === 'paged-flip'}
            searchOpen={searchOpen}
            query={searchQuery}
            onQueryChange={handleSearch}
            onSearchClose={() => {
              setSearchOpen(false);
            }}
            sections={placeNavSections}
            activeSectionId={activeCategoryId}
            onSectionChange={(value) => {
              // The app keeps the query when the tab changes, and the page
              // already narrows the search-filtered data by the active tab, so
              // these two filters compose instead of replacing each other.
              setActiveCategoryId(value);
              if (value) void trackCategoryViewed(menu?.id ?? '', { category: String(value) });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            groups={placeNavGroups}
            activeGroupId={activeCollectionId}
            onGroupChange={(value) => {
              setActiveCollectionId(value);
              setActiveCategoryId(null);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            hasDietaryProfile={hasDietaryProfile}
            hideDietaryClashes={hideDietaryClashes}
            onToggleDietaryClashes={() => setHideDietaryClashes((value) => !value)}
            onEditDietary={() => setDietaryPrefsOpen(true)}
            sticky={false}
            stickyTop="var(--place-header-height, calc(4rem + env(safe-area-inset-top)))"
            paletteStyle={guestPaletteStyle}
          />
        ) : (
        <PlaceMenuNav
          // The header is 56px plus its status line, which is 22px and only
          // present for a seated/counter/closed guest. `sticky` resolves against
          // a fixed offset, so this asks the browser for the header's real
          // height instead of a hardcoded guess that was wrong in both
          // directions — the old value assumed a 34px ribbon that always
          // existed, so the category row sat 34px too low on every plain menu.
          stickyTop="var(--place-header-height, calc(3.5rem + env(safe-area-inset-top)))"
          groups={placeNavGroups}
          activeGroupId={activeCollectionId}
          onGroupChange={(value) => {
            setSearchQuery('');
            setActiveCollectionId(value);
            setActiveCategoryId(null);
            pullRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sections={placeNavSections}
          activeSectionId={activeCategoryId}
          onSectionChange={(value) => {
            setSearchQuery('');
            setActiveCategoryId(value);
            // ANA-02 (doc 04 §2.4, A1/P2): record a category view. This is the
            // live category nav; the dead CategoryTabs inside
            // PublicMenuOrderingExperience is a legacy duplicate, so the
            // tracker is wired at the real handler rather than to a second nav.
            if (value) void trackCategoryViewed(menu?.id ?? '', { category: String(value) });
            pullRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
        )}


        {/* A sub-type flow owns the whole experience, so it replaces the menu
            body rather than appearing beneath it. */}
        {subTypeFlowBody ?? rendererContent}

        <PublicMenuPoweredFooter
          brandingLevel={brandingLevel}
          visible={footerVisible(shellConfig.footerStyle)}
          palette={templateSurfacePalette}
        />
      </PlaceShell>
      )}


      <PublicMenuPromotionClaimDialog
        claimResult={claimResult}
        claimStatusMessage={claimStatusMessage}
        claimMetaLine={claimResult ? formatClaimExpiry(claimResult.claim.expires_at) : ''}
        onOpenChange={(open) => !open && setClaimResult(null)}
        onCopyClaimCode={copyClaimCode}
        palette={templateSurfacePalette}
      />

      {/* The arrival question (doc 70 §3). Mounted only when there is genuinely
          more than one way to get food here and no QR has already answered it —
          see `arrivalOpen` for why dismissing it is safe. */}
      {arrivalOpen ? (
        <ArrivalSheet
          open={arrivalOpen}
          onOpenChange={setArrivalOpen}
          businessName={businessInfo.name || 'Order'}
          // Branch is settled by the time this renders — the resolver either
          // put it in the URL or showed the chooser. Switching location later
          // is the "Order from a different location" link in the branch sheet.
          branches={[]}
          selectedBranchId={branch?.id ?? null}
          modes={selectableModes.map((m) => toOrderContextMode(m.key))}
          destinations={arrivalDestinations}
          // Null, not the inferred mode: nothing has been chosen yet, so the
          // sheet must not render a "you're changing from X" warning at the
          // moment it is asking for the first time.
          currentMode={null}
          cartCount={0}
          onConfirm={({ mode, destination }) => {
            // A destination is a goal, not a fulfilment mode: "Reserve a
            // table" / "Plan an event" leave the menu for their own surface.
            // The cart is client-owned and branch-scoped, so it survives the
            // round trip untouched.
            if (destination) {
              window.location.href = destination.href;
              return;
            }
            if (mode) handleChooseFulfilment(fromOrderContextMode(mode));
          }}
          accentColor={templateSurfacePalette.accent}
          accentForeground={templateSurfacePalette.accentForeground}
        />
      ) : null}

      {/* Feedback island , lazy, secondary moment (doc 03 §3.1/§5.6). Mounted
          only once opened so it never ships in the first-load bundle. */}
      {feedbackOpen ? (
        <PublicMenuFeedbackDialog
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          tableLabel={tableSession?.table.label || 'this table'}
          rating={feedbackRating}
          onRatingChange={setFeedbackRating}
          note={feedbackNote}
          onNoteChange={setFeedbackNote}
          isSubmitting={isSubmittingFeedback}
          submitted={feedbackSubmitted}
          onSubmit={() => void handleSubmitServiceFeedback()}
          reviewUrl={reviewUrl}
          businessNoun={word(branch?.business_type, 'business', branch?.specific_type)}
          dialogClassName={dialogPresentationClassName(shellConfig.feedbackDialogStyle)}
          palette={templateSurfacePalette}
        />
      ) : null}

      {/* Dine-in ordering experience , requires table session */}
      {customerMode === 'dine_in' && (
        <div
          className={experienceV2 ? 'chow-root' : undefined}
          style={
            experienceV2
              // `.chow-root` is a page frame in chow/base.css (100dvh, page
              // background). This wrapper only needs its token scope: the
              // ordering island is a sibling of PlaceShell and paints fixed
              // sheets, so a second full-height page block would add a blank
              // screen after the menu.
              ? { ...guestPaletteStyle, minHeight: 0, background: 'transparent' }
              : templateSurfaceStyle
          }
        >
          <PublicMenuOrderingExperience
              menuId={menu?.id || ''}
              branchId={menu?.branch_id || initialMenu.branch_id || null}
              activeLineId={activeLineId}
              cartLines={cartLines}
              items={presentedMenuData?.menu?.items || effectiveMenuData?.menu?.items || []}
              tableSession={tableSession}
              branchOrderingEnabled={branchOrderingEnabled}
              serviceAreaMode="table"
              desktopLayout={experienceV2}
              chow={experienceV2}
              captureMenuItemClicks={!experienceV2}
              placeName={branch?.name || businessInfo.name || ''}
              placeImage={typeof businessInfo.logo_url === 'string' && businessInfo.logo_url ? businessInfo.logo_url : null}
              requireGuestName={false}
              requireCounterEntryCode={false}
              dialogPresentation={shellConfig.orderingDialogStyle}
              palette={templateSurfacePalette}
              onCartOpenChange={setOrderingCartOpen}
              onDeliveryCheckoutChange={setDeliveryCheckoutOpen}
              businessId={tableSession?.branch.business_id || business?.id || menu?.business_id || ''}
              businessSlug={businessSlug}
              historyHref={orderHistoryHref}
              customerName={customerDisplayName}
              customerContact={customerContact}
              flipbookPager={false}
              itemPromotions={itemPromotionsMap}
              onClaimPromotion={handleClaimPromotion}
              claimingPromotionId={claimingPromotionId}
              customerSignedIn={customerSignedIn}
              onRequestSignIn={requestCustomerSignIn}
              initialItemToOpen={browseOrderItem}
              onInitialItemOpened={() => setBrowseOrderItem(null)}
              initialCartOpen={pendingCartOpen}
              onInitialCartOpened={() => {
                setPendingCartOpen(false);
                setPendingCheckoutOpen(false);
              }}
              initialCheckoutStep={pendingCheckoutOpen}
              initialCart={initialCart}
              branchAllergenDeclaration={initialMenu?.branchAllergenDeclaration || {}}
            />
        </div>
      )}

      {/* Pickup ordering experience , no session required */}
      {customerMode === 'pickup' && (
        <div
          className={experienceV2 ? 'chow-root' : undefined}
          style={
            experienceV2
              // `.chow-root` is a page frame in chow/base.css (100dvh, page
              // background). This wrapper only needs its token scope: the
              // ordering island is a sibling of PlaceShell and paints fixed
              // sheets, so a second full-height page block would add a blank
              // screen after the menu.
              ? { ...guestPaletteStyle, minHeight: 0, background: 'transparent' }
              : templateSurfaceStyle
          }
        >
          <PublicMenuOrderingExperience
              menuId={menu?.id || ''}
              branchId={menu?.branch_id || initialMenu.branch_id || null}
              activeLineId={activeLineId}
              cartLines={cartLines}
              items={presentedMenuData?.menu?.items || effectiveMenuData?.menu?.items || []}
              // A scanned counter/till QR is a SESSION, not a session-free
              // pickup: the guest is physically at the till. Passing the
              // session lets the server resolve the counter table and issue a
              // queue number + claim code (counter_pickup_qr channel) instead
              // of silently downgrading the order to self-service pickup.
              // `serviceAreaMode="counter_pickup"` keeps the pickup checkout
              // UI (entry code, name, pay-at-counter) while `isSelfServiceMode`
              // stays false so the session token travels with the order.
              tableSession={isCounterPickupSession ? tableSession : null}
              branchOrderingEnabled={true}
              serviceAreaMode={isCounterPickupSession ? 'counter_pickup' : 'pickup'}
              // Chow asks the fulfilment question at checkout; this is the
              // switch. A counter-QR guest is physically at the till, so the
              // mode is a fact for them and the switch stays hidden.
              fulfilmentOptions={isCounterPickupSession ? [] : v2CheckoutFulfilment}
              onFulfilmentChange={handleChooseFulfilment}
              desktopLayout={experienceV2}
              chow={experienceV2}
              captureMenuItemClicks={!experienceV2}
              placeName={branch?.name || businessInfo.name || ''}
              placeImage={typeof businessInfo.logo_url === 'string' && businessInfo.logo_url ? businessInfo.logo_url : null}
              branchAddress={publicBranchDetails.address}
              branchPhone={publicBranchDetails.phone}
              accountRequired={accountRequiredForCheckout}
              contactRequired={contactRequiredForCheckout}
              requireGuestName={branchOrderingSettings.counterPickupRequireGuestName}
              requireCounterEntryCode={branchOrderingSettings.counterPickupRequireEntryCode}
              dialogPresentation={shellConfig.orderingDialogStyle}
              palette={templateSurfacePalette}
              onCartOpenChange={setOrderingCartOpen}
              onDeliveryCheckoutChange={setDeliveryCheckoutOpen}
              businessId={business?.id || menu?.business_id || ''}
              businessSlug={businessSlug}
              historyHref={orderHistoryHref}
              staffNoun={word(branch?.business_type, 'staffMember', branch?.specific_type)}
              customerName={customerDisplayName}
              customerContact={customerContact}
              flipbookPager={false}
              itemPromotions={itemPromotionsMap}
              onClaimPromotion={handleClaimPromotion}
              claimingPromotionId={claimingPromotionId}
              customerSignedIn={customerSignedIn}
              onRequestSignIn={requestCustomerSignIn}
              initialItemToOpen={browseOrderItem}
              onInitialItemOpened={() => setBrowseOrderItem(null)}
              initialCartOpen={pendingCartOpen}
              onInitialCartOpened={() => {
                setPendingCartOpen(false);
                setPendingCheckoutOpen(false);
              }}
              initialCheckoutStep={pendingCheckoutOpen}
              initialCart={initialCart}
              branchAllergenDeclaration={initialMenu?.branchAllergenDeclaration || {}}
            />
        </div>
      )}

      {/* Delivery ordering experience , no session required */}
      {customerMode === 'delivery' && !deliveryAction?.href && (
        <div
          className={experienceV2 ? 'chow-root' : undefined}
          style={
            experienceV2
              // `.chow-root` is a page frame in chow/base.css (100dvh, page
              // background). This wrapper only needs its token scope: the
              // ordering island is a sibling of PlaceShell and paints fixed
              // sheets, so a second full-height page block would add a blank
              // screen after the menu.
              ? { ...guestPaletteStyle, minHeight: 0, background: 'transparent' }
              : templateSurfaceStyle
          }
        >
          <PublicMenuOrderingExperience
              menuId={menu?.id || ''}
              branchId={menu?.branch_id || initialMenu.branch_id || null}
              activeLineId={activeLineId}
              cartLines={cartLines}
              items={presentedMenuData?.menu?.items || effectiveMenuData?.menu?.items || []}
              tableSession={null}
              branchOrderingEnabled={true}
              serviceAreaMode="delivery"
              fulfilmentOptions={v2CheckoutFulfilment}
              onFulfilmentChange={handleChooseFulfilment}
              desktopLayout={experienceV2}
              chow={experienceV2}
              captureMenuItemClicks={!experienceV2}
              placeName={branch?.name || businessInfo.name || ''}
              placeImage={typeof businessInfo.logo_url === 'string' && businessInfo.logo_url ? businessInfo.logo_url : null}
              branchAddress={publicBranchDetails.address}
              branchPhone={publicBranchDetails.phone}
              accountRequired={accountRequiredForCheckout}
              scheduledPickupTime={nextOpenScheduledTime}
              requireGuestName={true}
              requireCounterEntryCode={false}
              dialogPresentation={shellConfig.orderingDialogStyle}
              palette={templateSurfacePalette}
              onCartOpenChange={setOrderingCartOpen}
              onDeliveryCheckoutChange={setDeliveryCheckoutOpen}
              businessId={business?.id || menu?.business_id || ''}
              businessSlug={businessSlug}
              historyHref={orderHistoryHref}
              staffNoun={word(branch?.business_type, 'staffMember', branch?.specific_type)}
              branchLocation={branchLocation}
              customerName={customerDisplayName}
              customerContact={customerContact}
              flipbookPager={false}
              itemPromotions={itemPromotionsMap}
              onClaimPromotion={handleClaimPromotion}
              claimingPromotionId={claimingPromotionId}
              customerSignedIn={customerSignedIn}
              onRequestSignIn={requestCustomerSignIn}
              initialItemToOpen={browseOrderItem}
              onInitialItemOpened={() => setBrowseOrderItem(null)}
              initialCartOpen={pendingCartOpen}
              onInitialCartOpened={() => {
                setPendingCartOpen(false);
                setPendingCheckoutOpen(false);
              }}
              initialCheckoutStep={pendingCheckoutOpen}
              initialCart={initialCart}
              branchAllergenDeclaration={initialMenu?.branchAllergenDeclaration || {}}
            />
        </div>
      )}

      {/* Scheduled pre-order experience , slot picker + ordering */}
      {customerMode === 'scheduled' && (
        <div
          className={experienceV2 ? 'chow-root' : undefined}
          style={
            experienceV2
              // `.chow-root` is a page frame in chow/base.css (100dvh, page
              // background). This wrapper only needs its token scope: the
              // ordering island is a sibling of PlaceShell and paints fixed
              // sheets, so a second full-height page block would add a blank
              // screen after the menu.
              ? { ...guestPaletteStyle, minHeight: 0, background: 'transparent' }
              : templateSurfaceStyle
          }
        >
          <PublicMenuScheduledOrderingExperience
            menuId={menu?.id || ''}
            items={presentedMenuData?.menu?.items || effectiveMenuData?.menu?.items || []}
            branchOrderingEnabled={true}
            dialogPresentation={shellConfig.orderingDialogStyle}
            palette={templateSurfacePalette}
            businessId={business?.id || menu?.business_id || ''}
            businessSlug={businessSlug}
            customerName={customerDisplayName}
            customerContact={customerContact}
            flipbookPager={false}
            itemPromotions={itemPromotionsMap}
            onClaimPromotion={handleClaimPromotion}
            claimingPromotionId={claimingPromotionId}
            customerSignedIn={customerSignedIn}
            onRequestSignIn={requestCustomerSignIn}
            contactRequired={contactRequiredForCheckout}
            initialItemToOpen={browseOrderItem}
            onInitialItemOpened={() => setBrowseOrderItem(null)}
            initialCartOpen={pendingCartOpen}
            onInitialCartOpened={() => setPendingCartOpen(false)}
            initialCart={initialCart}
            branchAllergenDeclaration={initialMenu?.branchAllergenDeclaration || {}}
            // When no slots are open, offer the branch's other fulfilment
            // modes so the guest is never stuck on a dead-end picker.
            availableModes={orderModes
              .filter((mode) => mode.key !== 'scheduled')
              .map((mode) => ({
                key: mode.key,
                label: mode.label,
                hint: mode.description ?? null,
              }))}
            onModeChange={handleChooseFulfilment}
          />
        </div>
      )}

      {/* doc 64 §8 — the guest assistant.
          Opened from the header pill (see `trailing` above), not a floating
          bubble: the bottom-right corner belongs to the guest's order. The
          panel stays mounted across an `ai-mode-change` so a conversation is
          not torn down when the assistant switches the surface to pickup or
          dine-in mid-order. `customerChatEnabled` (configs/ai/customer-chat.json)
          gates the mount: CustomerAssistant has hooks, so the switch cannot
          live inside it the way it lives inside AiEntryPill. */}
      {assistantBusinessId && customerChatEnabled() && !isEmbeddedView ? (
        <div style={placeChromeStyle}>
          <CustomerAssistant
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
            startInVoice={assistantVoice}
            businessId={assistantBusinessId}
            branchId={tableSession?.branch.id || branch?.id || undefined}
            // The tab the guest is reading. Lets the assistant say "that is on
            // the Bar menu" rather than report an add this page would drop.
            lineSlug={activeLineSlug ?? undefined}
            placeName={businessInfo.name}
            language={selectedLanguage}
            translateItemTitle={assistantItemTitle}
            // The voice resolver's view of the menu (doc 64 §9). Null when the
            // menu is empty — the resolver falls through to the model.
            menuIndex={assistantMenuIndex}
          />
        </div>
      ) : null}

      {/* Browse-mode item detail sheet (view-only, no ordering). V2 wears the
          Chow item screen; V1 keeps the compact platform summary. */}
      {experienceV2 ? (
        <GuestV2MenuItemSheet
          item={browseDetailItem}
          open={browseDetailOpen}
          onOpenChange={(open) => { setBrowseDetailOpen(open); if (!open) setBrowseDetailItem(null); }}
          dietaryProfile={dietaryProfile}
          paletteStyle={guestPaletteStyle}
          addLabel="Add to cart"
          onAdd={
            orderingReachable && !usesSubTypeOrderFlow && browseDetailItem
              ? (qty: number, note: string) => {
                  const item = browseDetailItem;
                  setBrowseDetailOpen(false);
                  setBrowseDetailItem(null);
                  if (customerMode === 'menu') {
                    const first = customerModes.find((mode) => mode.key !== 'menu');
                    if (first) handleCustomerModeChange(first);
                  }
                  setBrowseOrderItem(item);
                  // Pass note/qty via sessionStorage as a quick bridge
                  try {
                    if (typeof window !== 'undefined') {
                      window.sessionStorage.setItem('mnrq_v2_quick_add', JSON.stringify({ qty, note }));
                    }
                  } catch {}
                }
              : undefined
          }
          onOrderNow={
            orderingReachable && !usesSubTypeOrderFlow && browseDetailItem
              ? (qty: number, note: string) => {
                  const item = browseDetailItem;
                  setBrowseDetailOpen(false);
                  setBrowseDetailItem(null);
                  if (customerMode === 'menu') {
                    const first = customerModes.find((mode) => mode.key !== 'menu');
                    if (first) handleCustomerModeChange(first);
                  }
                  setBrowseOrderItem(item);
                  try {
                    if (typeof window !== 'undefined') {
                      window.sessionStorage.setItem('mnrq_v2_quick_add', JSON.stringify({ qty, note, goCheckout: true }));
                    }
                  } catch {}
                }
              : undefined
          }
          unavailableHint={
            orderingReachable
              ? null
              : branchOrderingEnabled
                ? 'Scan the QR code at your table to order this.'
                : null
          }
        />
      ) : (
        <MenuItemDetailSheet
          item={browseDetailItem}
          open={browseDetailOpen}
          onOpenChange={(open) => { setBrowseDetailOpen(open); if (!open) setBrowseDetailItem(null); }}
          palette={templateSurfacePalette}
          surfaceStyle={templateSurfaceStyle}
          addLabel={word(branch?.business_type, 'placeOrderCta', branch?.specific_type)}
          onAdd={
            orderingReachable && !usesSubTypeOrderFlow && browseDetailItem
              ? () => {
                  const item = browseDetailItem;
                  setBrowseDetailOpen(false);
                  setBrowseDetailItem(null);
                  if (customerMode === 'menu') {
                    const first = customerModes.find((mode) => mode.key !== 'menu');
                    if (first) handleCustomerModeChange(first);
                  }
                  setBrowseOrderItem(item);
                }
              : undefined
          }
          unavailableHint={
            orderingReachable
              ? null
              : branchOrderingEnabled
                ? 'Scan the QR code at your table to order this.'
                : null
          }
        />
      )}

      {/* Reattaches an already-permitted device to this guest's orders.
          Deliberately at the page root rather than inside the ordering
          experience, which only mounts for dine-in with a table session , a
          guest browsing any other menu would have been skipped entirely.
          Renders nothing and never prompts. */}
      {/* Search filters the menu underneath live, so it is a bar over the
          content rather than a drawer that hides the results. V2 puts that
          field inside the menu itself (`GuestV2MenuNav`), so the sheet would
          be a second, visually different search for the same page. */}
      {experienceV2 ? null : (
        <PlaceSearchSheet
          open={searchOpen}
          value={searchQuery}
          onChange={handleSearch}
          onClose={() => {
            setSearchOpen(false);
            handleSearch('');
          }}
          resultCount={searchResultCount}
          style={placeChromeStyle}
        />
      )}

      {/* The dietary profile editor behind the V2 menu's "Edit" entry. Lazy so
          a guest who never opens it does not download it. */}
      {experienceV2 ? (
        <DietaryPreferencesSheet
          open={dietaryPrefsOpen}
          onOpenChange={setDietaryPrefsOpen}
          contentStyle={guestPaletteStyle}
        />
      ) : null}

      {/* Account, orders, loyalty and gift cards — the "You" side of the place.
          Reached from the header on every screen instead of from a panel that
          only existed on the menu. V2 gets the Chow profile sheet; V1 keeps its
          right-hand sheet untouched. */}
      {experienceV2 ? (
        <GuestV2ProfileSheet
          open={accountOpen}
          onOpenChange={setAccountOpen}
          placeName={branch?.name || businessInfo.name || 'This place'}
          accountHref={accountHref ?? placeBasePath}
          activityHref={orderHistoryHref ?? placeBasePath}
          onSignIn={() => {
            setAccountOpen(false);
            setAuthOpen(true);
          }}
          onEditDietary={() => {
            setAccountOpen(false);
            setDietaryPrefsOpen(true);
          }}
          onFeedback={() => {
            setAccountOpen(false);
            setFeedbackOpen(true);
          }}
          selectedLanguage={selectedLanguage}
          onSelectLanguage={setSelectedLanguage}
          isTranslating={isTranslating}
          paletteStyle={guestPaletteStyle}
        />
      ) : (
      <Sheet open={accountOpen} onOpenChange={setAccountOpen}>
        <SheetContent side="right" className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-b border-[var(--color-border-subtle)] px-4 py-3">
            <SheetTitle className="font-[family-name:var(--font-place-control)] text-base">
              You
            </SheetTitle>
          </SheetHeader>

          {/* "My orders" first: a guest opening this mid-meal is looking for
              the order they already placed, not for an account. */}
          <PlaceOrdersEntry onOpen={() => setAccountOpen(false)} />

          {/* Book / Reserve. This was a quarter of the retired bottom rail —
              permanent furniture for something most guests on a menu never do.
              It keeps its own gating (`resolvePlaceTabs` only produces it where
              the branch genuinely takes bookings) and is one tap away instead of
              occupying the screen for the whole visit. */}
          {bookDestination ? (
            <Link
              href={bookDestination.absoluteHref ?? placeBasePath}
              onClick={() => setAccountOpen(false)}
              className="flex w-full items-center gap-3 border-b border-[var(--color-border-subtle)] px-4 py-3.5 text-left font-[family-name:var(--font-place-control)] text-sm transition-colors hover:bg-[var(--color-bg-surface-hover)]"
            >
              <bookDestination.icon aria-hidden="true" className="size-5 shrink-0 text-[var(--color-text-secondary)]" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-[var(--color-text-primary)]">
                  {bookDestination.label}
                </span>
                <span className="block text-xs text-[var(--color-text-secondary)]">
                  Hold a table or an event date at {branch?.name || businessInfo.name}
                </span>
              </span>
            </Link>
          ) : null}

          {/* Chow — the customer's own app.
              It was reachable only as a button labelled "Customer dashboard",
              buried inside the signed-in half of the account panel, pointing at
              `customerUrl('/')` — which resolves to `/`, the MARKETING HOMEPAGE,
              on every dev and preview build. So the one link from a menu to the
              consumer product was invisible to signed-out guests, named after an
              internal concept, and broken outside production.
              It sits here with the guest's other things, is offered whether or
              not they are signed in (Chow handles its own sign-in, and a
              signed-out guest is exactly who needs to find out it exists), and
              uses `chowAppUrl` — a navigation-only helper, because customerUrl
              doubles as the API base and must stay a bare path in dev. */}
          <a
            href={chowAppUrl('/')}
            className="flex w-full items-center gap-3 border-b border-[var(--color-border-subtle)] px-4 py-3.5 text-left font-[family-name:var(--font-place-control)] text-sm transition-colors hover:bg-[var(--color-bg-surface-hover)]"
          >
            <UtensilsCrossed aria-hidden="true" className="size-5 shrink-0 text-[var(--color-text-secondary)]" />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-[var(--color-text-primary)]">Open Chow</span>
              <span className="block text-xs text-[var(--color-text-secondary)]">
                Every place you&rsquo;ve ordered from, your wallet and receipts
              </span>
            </span>
            <ArrowUpRight aria-hidden="true" className="size-4 shrink-0 opacity-50" />
          </a>


          <CustomerAccountAffordance
            businessSlug={businessSlug}
            businessId={tableSession?.branch.business_id || business?.id || menu?.business_id || null}
            palette={templateSurfacePalette}
            chow={experienceV2}
          />
          {accountHref ? (
            <Link
              href={accountHref}
              onClick={() => setAccountOpen(false)}
              className="flex min-h-14 w-full items-center justify-between border-t border-[var(--color-border-subtle)] px-4 py-3 text-left font-[family-name:var(--font-place-control)] text-sm font-semibold text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-surface-hover)]"
            >
              <span>Profile and account</span>
              <ArrowUpRight aria-hidden="true" className="size-4 text-[var(--color-text-secondary)]" />
            </Link>
          ) : null}
        </SheetContent>
      </Sheet>
      )}

      {experienceV2 ? (
        <CustomerAuthModal
          chow
          open={authOpen}
          onOpenChange={setAuthOpen}
          onAuthenticated={() => void refreshCustomerSession()}
        />
      ) : null}

      <PushOrderSync />

      <BrowseCartFallback
        pendingCartOpen={pendingCartOpen}
        customerMode={customerMode}
        bestAvailableOrderMode={bestAvailableOrderMode}
        customerModes={customerModes}
        onSwitchMode={handleCustomerModeChange}
        onPendingChange={setPendingCartOpen}
      />
    </PlaceProvider>
  );
}

/**
 * Browse-mode cart opening. The ordering island owns the real openCart while
 * it is mounted; when none is (browse mode), this child keeps the ribbon's
 * cart tap alive: it switches into the best available ordering mode and opens
 * the cart sheet the moment that surface arrives. Lives as a CHILD of
 * <PlaceProvider> (not MenuDisplayPage itself, which renders the provider) so
 * it can read the place context.
 */
function BrowseCartFallback({
  pendingCartOpen,
  customerMode,
  bestAvailableOrderMode,
  customerModes,
  onSwitchMode,
  onPendingChange,
}: {
  pendingCartOpen: boolean;
  customerMode: PublicMenuMode['key'];
  bestAvailableOrderMode: PublicMenuMode['key'] | null;
  customerModes: PublicMenuMode[];
  onSwitchMode: (mode: PublicMenuMode) => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const openCart = useCallback(() => {
    if (pendingCartOpen) return;
    const mode = customerModes.find((m) => m.key === bestAvailableOrderMode);
    if (!mode) return;
    onPendingChange(true);
    onSwitchMode(mode);
  }, [pendingCartOpen, bestAvailableOrderMode, customerModes, onSwitchMode, onPendingChange]);

  // Register ONLY while browsing. This component mounts after the ordering
  // island, and PlaceProvider's registry is last-writer-wins for any slot that
  // is not `undefined` — so registering unconditionally clobbered the island's
  // real openCart with a fallback that began `if (customerMode !== 'menu')
  // return`. The moment a guest added an item (which switches the mode to
  // pickup/delivery), the action bar's own "Review order" became a no-op and
  // the cart could never be opened. Handing back `undefined` outside browse
  // mode lets the island's handler stay in the slot, which is what the "last
  // mounted owner wins" contract was meant to express.
  useRegisterPlaceActions({ openCart: customerMode === 'menu' ? openCart : undefined });
  return null;
}
